import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, ElementRef, ViewChild, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EntityPickerComponent, PickerOption, PickerSearchPage } from '../../../shared/components/form/entity-picker/entity-picker.component';
import { EntitySelectComponent, EntitySelectPage } from '../../../shared/components/entity-select/entity-select.component';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { Contact } from '../../contactos/models/contacto.model';
import { QuoteApiService } from '../services/quote-api.service';
import { QuoteStatusApiService } from '../services/quote-status-api.service';
import { QuoteApprovalApiService } from '../services/quote-approval-api.service';
import { QuoteClauseApiService } from '../services/quote-clause-api.service';
import { PriceListApiService } from '../../productos/services/price-list-api.service';
import { PriceListResponse } from '../../productos/models/price-list.model';
import { DiscountRuleApiService } from '../../productos/services/discount-rule-api.service';
import { ResolveDiscountResponse, DiscountRuleResponse, ApplyCouponResponse } from '../../productos/models/discount-rule.model';
import { ProductoVariantApiService } from '../../productos/services/producto-variant-api.service';
import { ProductVariantResponse } from '../../productos/models/producto-variant.model';
import { CrmUserResponse } from '../../users/models/user.model';
import { QuoteClauseResponse } from '../models/quote-clause.model';
import { QuoteApprovalResponse, QUOTE_STATUS_PENDING_APPROVAL, QUOTE_STATUS_APPROVED_INTERNAL } from '../models/quote-approval.model';
import { QuoteRequestApprovalModalComponent } from '../shared/quote-request-approval-modal/quote-request-approval-modal.component';
import { QuoteStatusBadgeComponent } from '../shared/quote-status-badge/quote-status-badge.component';
import { QuoteAttachmentsComponent } from '../shared/quote-attachments/quote-attachments.component';
import { DealApiService } from '../../deals/services/deal-api.service';
import { DealContactApiService } from '../../deals/services/deal-contact-api.service';
import { DealContactResponse } from '../../deals/models/deal.model';
import { ProductoApiService } from '../../productos/services/producto-api.service';
import { ProductoTierPriceApiService } from '../../productos/services/producto-tier-price-api.service';
import { ProductBundleApiService } from '../../productos/services/product-bundle-api.service';
import { ProductBundleItemResponse } from '../../productos/models/product-bundle.model';
import { QuoteContentTemplateApiService } from '../services/quote-content-template-api.service';
import { QuoteContentTemplateResponse } from '../models/quote-content-template.model';
import { QuoteRequest, QuoteResponse, QuoteItemRequest, AddBundleRequest, QuoteVersionResponse, buildPublicQuoteUrl, EmailRecipient, SendQuoteEmailRequest, ResponsibleSignRequest, QuotePaymentType } from '../models/quote.model';
import { QuoteDocumentComponent } from '../shared/quote-document/quote-document.component';
import { QuoteDocumentData, quoteDocumentFromResponse } from '../shared/quote-document/quote-document.model';
import { QuoteStatusResponse } from '../models/quote-status.model';
import { DealResponse } from '../../deals/models/deal.model';
import { ProductResponse, CurrencyResponse } from '../../productos/models/producto.model';
import { ProductBundleResponse } from '../../productos/models/product-bundle.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { BusinessApiService } from '../../businesses/services/business-api.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { QuoteFormModalService } from '../services/quote-form-modal.service';

// Chip de destinatario del modal "Enviar al cliente" (estado de UI, no viaja al backend tal cual).
// También se usa como "sugerencia": contacto del negocio con correo aún no agregado como chip.
interface SendRecipientChip {
  email: string;
  name?: string;
  isPrimary?: boolean;
  roleName?: string;
  // true si proviene de un contacto del negocio: al quitar el chip vuelve a las sugerencias
  fromContact?: boolean;
}

/**
 * Valor crudo de una fila del FormArray de ítems. Es el QuoteItemRequest del contrato MÁS los
 * campos de presentación que solo viven en el form (`variantName`/`variantSku`, resueltos por el
 * backend en la respuesta para poder mostrar la variante en la fila). buildQuoteRequest los quita
 * antes de armar el request.
 */
interface ItemFormValue extends QuoteItemRequest {
  variantName?: string | null;
  variantSku?: string | null;
}

/**
 * (Incidente 2c) Validador del descuento global: debe ser positivo (>= 0) y, cuando el tipo es
 * porcentaje, no superar 100. Lee el tipo desde el control hermano `globalDiscountType`.
 */
function globalDiscountValidator(control: AbstractControl): ValidationErrors | null {
  const raw = control.value;
  if (raw === null || raw === undefined || raw === '') return null;
  const v = Number(raw);
  if (isNaN(v)) return null;
  if (v < 0) return { min: { min: 0 } };
  const type = control.parent?.get('globalDiscountType')?.value;
  if (type === 'percent' && v > 100) return { discountPercentMax: true };
  return null;
}

/**
 * (INC_CRM_005 (5) inc41) Subtipo de la pestaña "Productos" del selector de ítems:
 * Con Inventario (product.type = 'inventory') / Sin Inventario ('no_inventory').
 * Es un filtro de catálogo del frontend; el itemType que viaja al backend sigue
 * siendo 'product' en ambos casos.
 */
export type ProductKind = 'inventory' | 'no_inventory';

@Component({
  selector: 'app-quotes-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    DragDropModule,
    EntityPickerComponent,
    EntitySelectComponent,
    QuoteRequestApprovalModalComponent,
    QuoteAttachmentsComponent,
    QuoteDocumentComponent,
    QuoteStatusBadgeComponent
  ],
  templateUrl: './quotes-form.component.html'
})
export class QuotesFormComponent implements OnInit, OnChanges, OnDestroy {
  // ------------------------------------------------------------
  // Modo modal (abierto desde la lista) vs modo ruta (deep-link)
  // ------------------------------------------------------------
  // modalMode = true  → el wizard se abrió como overlay desde la lista; cerrar/guardar emiten eventos
  //                      y NO navegan. El id a editar llega por modalQuoteId.
  // modalMode = false → el wizard se renderiza por el router-outlet (deep-link /cotizaciones/crear|editar);
  //                      cerrar/guardar navegan de vuelta a /cotizaciones. El id llega por la ruta.
  // En ambos casos la UI se pinta como modal fullscreen.
  @Input() modalMode = false;
  @Input() modalQuoteId: number | null = null;
  /** Abrir el modal de envío tras cargar (modo modal; equivale a ?enviar=1 de la ruta). */
  @Input() modalSendEmail = false;
  /**
   * Negocio a PRESELECCIONAR al crear una cotización nueva (abrir desde el detalle/kanban/lista de
   * negocios). Solo aplica en alta (sin modalQuoteId): fija el negocio en el paso 1 y precarga su
   * contacto primario. Se ignora al editar.
   */
  @Input() modalDealId: number | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() savedQuote = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  publishing = false;
  rotating = false;
  revoking = false;
  error = '';
  successMessage = '';
  isEditing = false;
  quoteId: number | null = null;

  // Enlace público revelado UNA vez (al publicar/rotar). Se conserva en memoria para poder copiarlo
  // mientras dure la sesión del formulario; los GET posteriores ya no lo devuelven.
  publicLinkUrl: string | null = null;

  // Historial de versiones (misma familia / mismo quoteNumber)
  showVersionsModal = false;
  versions: QuoteVersionResponse[] = [];
  loadingVersions = false;
  restoringVersionId: number | null = null;
  creatingVersion = false;

  // Duplicar cotización
  duplicating = false;

  // Aprobación interna (RF-001): modal de solicitud + solicitud PENDING vigente (para el hint)
  showRequestApprovalModal = false;
  pendingApproval: QuoteApprovalResponse | null = null;

  // Envío por correo (#28)
  showSendEmailModal = false;
  sendingEmail = false;
  sendEmailError = '';
  // Destinatarios como chips (solo el contacto principal precargado + agregados a mano o desde sugerencias)
  sendRecipients: SendRecipientChip[] = [];
  sendRecipientInput = '';
  sendRecipientInputError = '';
  loadingDealContacts = false;
  // Demás contactos del negocio con correo: píldoras de sugerencia clicables (no se envían hasta agregarse)
  sendSuggestions: SendRecipientChip[] = [];
  showAllSuggestions = false;
  readonly SUGGESTIONS_VISIBLE = 4;
  // Contactos del negocio sin correo (solo informativo, no seleccionables)
  dealContactsSinCorreo: string[] = [];
  sendCc = '';
  sendPersonalMessage = '';
  readonly SEND_EMAIL_MESSAGE_MAX = 2000;
  // Abrir el modal de envío al cargar (navegación desde la lista con ?enviar=1)
  private openSendEmailAfterLoad = false;

  // Catalogs
  quoteStatuses: QuoteStatusResponse[] = [];
  deals: DealResponse[] = [];
  products: ProductResponse[] = [];
  bundles: ProductBundleResponse[] = [];
  currencies: CurrencyResponse[] = [];
  loadingCatalogs = false;

  // Listas de precios del negocio (V75): selector en el paso 1.
  priceLists: PriceListResponse[] = [];
  // Supervisores: usuarios activos del negocio (reusa el endpoint del selector de aprobadores).
  supervisors: CrmUserResponse[] = [];

  // Moneda por defecto del negocio (GET /api/businesses/{id}). null = sin moneda configurada.
  // Se resuelve junto con los catálogos en CREATE para preseleccionar currencyId.
  private businessDefaultCurrencyId: number | null = null;

  // Cláusulas legales (V72): catálogo + selección (ids) de la cotización.
  clauses: QuoteClauseResponse[] = [];
  selectedClauseIds = new Set<number>();
  showClauses = false;

  // Current quote data
  currentQuote: QuoteResponse | null = null;

  // Searchable picker selection (view state over the dealId form control)
  dealPickerSelected: PickerOption | null = null;

  // ------------------------------------------------------------
  // Selector de cliente/contacto (idContactPrimary) — Paso 1
  // ------------------------------------------------------------
  // Texto a mostrar en el buscador de contacto cuando el value se fija externamente (precarga del
  // negocio o edición). El selector es editable: el usuario puede buscar y cambiar a cualquier contacto.
  contactPickerDisplay = '';
  // true una vez que el usuario eligió manualmente un contacto: evita que un re-disparo de la precarga
  // del MISMO negocio pise su elección. Cambiar de negocio lo resetea (precarga el primario del nuevo).
  private contactManuallyChosen = false;

  // Today's date in YYYY-MM-DD (default for date pickers)
  readonly today: string;

  // (Incidente 18) Fecha mínima de EMISIÓN (hoy − 3 días, YYYY-MM-DD). El backend rechaza fechas
  // de emisión anteriores con 400. Se valida en cliente para bloquear el submit con mensaje claro.
  readonly minIssuedDate: string;
  // Valor de emisión cargado de una cotización existente (YYYY-MM-DD). En EDIT solo se reenvía
  // datetimeIssued si el usuario lo cambió, para no romper cotizaciones antiguas (>3 días).
  private originalIssued = '';
  // Valor de «Válido hasta» cargado de una cotización existente (YYYY-MM-DD). Misma idea que
  // `originalIssued`: una cotización YA vencida tiene esa fecha en el pasado por definición, y
  // no puede quedar ineditable por ello.
  private originalExpires = '';

  // (Incidente 13) Datos visibles del cliente cuando se crea/edita una cotización: contacto principal
  // del negocio (nombre, correo, teléfono). La empresa se resuelve del catálogo de negocios por dealId.
  dealClientInfo: { name: string; email?: string; phone?: string } | null = null;

  // Default "Válido Hasta" en CREATE: hoy + 30 días (YYYY-MM-DD). Así el backend deriva
  // validityDays/currencyLabel y el documento muestra "Válido por N días" sin quedar en "—".
  readonly defaultValidUntil: string;
  // Días de validez por defecto al crear una cotización nueva.
  private readonly DEFAULT_VALIDITY_DAYS = 30;
  // Código ISO de la moneda preferida por defecto al crear (negocio peruano: Soles).
  private readonly DEFAULT_CURRENCY_CODE = 'PEN';


  // Collapsible text sections
  showNotes = false;
  showOpeningMessage = false;
  showInternalNotes = false;
  showTerms = false;
  showVersionNotes = false;

  // Add item panel (separate form, not part of FormArray until confirmed)
  showAddItemForm = false;
  // (Incidente 24) Tipos ofrecidos: Productos (product) y Servicios → Recursos (service_resource) /
  // Consultoría (service_consulting). 'custom' = ítem libre. 'bundle' ya no se ofrece.
  addFormItemType: 'custom' | 'product' | 'service_resource' | 'service_consulting' = 'custom';
  // (INC_CRM_005 (5) inc41) Subtipo de "Productos", espejo de Recursos/Consultoría en Servicios.
  // NO viaja al backend: itemType sigue siendo 'product' en ambos casos. Es un filtro del
  // catálogo, igual que el subtipo de servicios (ver filterProductsByTerm).
  addFormProductKind: ProductKind = 'inventory';
  addFormItemName = '';
  addFormItemCode = '';
  addFormCategory = '';
  addFormDescription = '';
  addFormQuantity = 1;
  addFormUnit = '';
  addFormUnitPrice = 0;
  addFormDiscountPercent = 0;
  addFormDiscountAmount = 0;
  addFormProductId: number | null = null;
  addFormBundleId: number | null = null;
  addFormProductSearch = '';
  addFormBundleSearch = '';
  showAddFormProductDropdown = false;
  showAddFormBundleDropdown = false;

  // Inline item editing
  expandedItems = new Set<number>();
  itemProductSearchTerms: Record<number, string> = {};
  // (INC_CRM_005 (5) inc41) Subtipo de "Productos" por línea del FormArray (mismo criterio que
  // addFormProductKind). Sin entrada => 'inventory'.
  itemProductKinds: Record<number, ProductKind> = {};
  itemBundleSearchTerms: Record<number, string> = {};
  showItemProductDropdown: Record<number, boolean> = {};
  showItemBundleDropdown: Record<number, boolean> = {};

  // Precios por volumen (#18): filas cuyo precio unitario vino de los tramos del producto.
  // El precio sigue siendo editable: el tier es un autocompletado, no una imposición.
  tierAppliedItems: Record<number, boolean> = {};
  addFormTierApplied = false;

  // Reglas de descuento vigentes (Inc 40): descuento resuelto y auto-aplicado por línea.
  // El descuento se refleja en los campos Desc.%/Desc. Fijo de la línea (el cálculo lo toma
  // calculateItemSubtotal) y se muestra un aviso con el monto/total/mensaje de la regla.
  itemDiscountRules: Record<number, ResolveDiscountResponse> = {};
  // Descuento resuelto para el panel "Agregar ítem" (aún no confirmado en el FormArray).
  addFormDiscountRule: ResolveDiscountResponse | null = null;

  // ------------------------------------------------------------
  // (INC32) Reglas de descuento VISIBLES y gobernables por línea
  // ------------------------------------------------------------
  // El auto-aplicado del Inc 40 sigue siendo el comportamiento por DEFECTO (choice = 'auto').
  // Sobre él se añade control explícito: elegir una regla concreta, aplicar un cupón o
  // desactivar el descuento. El descuento final SIEMPRE se escribe en discountAmount porque
  // el backend calculateTotals ignora discountPercent.
  //
  //   'auto'      → resolverDescuentoLinea() manda (Inc 40 intacto).
  //   'rule:<id>' → el usuario forzó una regla concreta del catálogo de aplicables.
  //   'coupon'    → descuento venido de un cupón (POST /apply-coupon).
  //   'none'      → el usuario desactivó el descuento; el auto-aplicado no vuelve a pisarlo.
  itemDiscountChoice: Record<number, string> = {};
  // Reglas aplicables por línea (GET /api/product-discount-rules/applicable), ya filtradas
  // a los tipos elegibles ('volume' | 'contractual') y sin las vencidas.
  itemApplicableRules: Record<number, DiscountRuleResponse[]> = {};
  // Cupón por línea: código tecleado, error de validación y respuesta resuelta del backend.
  // El cupón se pide UNA vez (POST /apply-coupon incrementa sus usos); los recálculos por cambio
  // de cantidad/precio se hacen en cliente sobre la respuesta guardada, sin volver a consumirlo.
  itemCouponCode: Record<number, string> = {};
  itemCouponError: Record<number, string> = {};
  itemCouponResult: Record<number, ApplyCouponResponse> = {};
  applyingCoupon: Record<number, boolean> = {};

  // ------------------------------------------------------------
  // (INC33) Variantes de producto por línea
  // ------------------------------------------------------------
  // Variantes activas del producto de cada línea. Si el producto no tiene variantes, la clave
  // no existe y el selector NO se renderiza (sin ruido visual).
  itemVariants: Record<number, ProductVariantResponse[]> = {};
  // true cuando el precio unitario de la línea lo puso la variante y el usuario no lo tocó:
  // en ese caso el request viaja con unitPrice=0 para que el backend resuelva el precio efectivo.
  variantPriceApplied: Record<number, boolean> = {};
  // Variantes del panel "Agregar ítem" (aún no confirmado en el FormArray).
  addFormVariants: ProductVariantResponse[] = [];
  addFormVariantId: number | null = null;
  addFormVariantName: string | null = null;
  addFormVariantSku: string | null = null;
  addFormVariantPriceApplied = false;

  // ------------------------------------------------------------
  // (INC34) Añadir un PAQUETE al paso 2
  // ------------------------------------------------------------
  // El paquete NO es un tipo de ítem (el Inc 24 quitó 'bundle' del toggle a propósito): se
  // EXPANDE en N líneas normales de tipo 'product', una por componente, con bundleId para trazar.
  showAddBundleForm = false;
  addingBundle = false;
  bundleSearch = '';
  showBundleDropdown = false;
  selectedBundleId: number | null = null;

  // Importar ítems desde una plantilla de contenido (V76).
  showImportItemsModal = false;
  contentTemplates: QuoteContentTemplateResponse[] = [];
  loadingContentTemplates = false;
  importingTemplateId: number | null = null;

  // ------------------------------------------------------------
  // Asistente por pasos (wizard)
  // ------------------------------------------------------------
  // El formulario es UNO solo (mismo FormGroup); el wizard solo controla qué sección se muestra.
  // El cálculo de totales en vivo sigue corriendo aunque el paso 3 no esté visible.
  currentStep = 1;
  readonly totalSteps = 4;

  // ------------------------------------------------------------
  // Paso 2 — Buscador + Filtros (filtrado en cliente sobre el FormArray)
  // ------------------------------------------------------------
  // El filtro NO modifica el FormArray: solo decide qué índices se muestran (visibleItemIndices).
  // Mientras hay filtro activo se deshabilita el drag&drop para no reordenar una lista parcial.
  itemSearchTerm = '';
  itemTypeFilter: '' | 'product' | 'service_resource' | 'service_consulting' | 'custom' = '';
  itemCategoryFilter = '';

  // ------------------------------------------------------------
  // Paso 4 — Vista previa del PDF real (GET /api/quotes/{id}/pdf como blob → iframe)
  // ------------------------------------------------------------
  pdfLoading = false;
  pdfError = false;                          // true → mostrar fallback (quote-document HTML)
  pdfSafeUrl: SafeResourceUrl | null = null; // object URL saneado para el iframe
  private pdfObjectUrl: string | null = null; // object URL crudo para revocarlo

  // ------------------------------------------------------------
  // Paso 4 — Revisión y Envío
  // ------------------------------------------------------------
  // Al entrar al paso 4 se persiste la cotización (create/update) y se recarga su QuoteResponse
  // para alimentar la vista previa del documento con datos resueltos por el backend.
  savingForReview = false;          // guardando al entrar al paso 4
  reviewDoc: QuoteDocumentData | null = null;  // datos normalizados para <app-quote-document>

  // Firma del responsable
  showResponsibleSignModal = false;
  responsibleSigning = false;       // POST /responsible-sign en curso
  responsibleSignError = '';
  signTab: 'registered' | 'drawn' | 'typed' | 'upload' = 'registered';
  typedSignatureName = '';          // texto para la firma tipográfica
  uploadedSignature: string | null = null;  // data URL de la imagen subida
  uploadSignatureError = '';
  // Canvas de firma manuscrita (mismo patrón del portal público)
  private signCanvasEl?: HTMLCanvasElement;
  private signCtx: CanvasRenderingContext2D | null = null;
  private signDrawing = false;
  private signLastX = 0;
  private signLastY = 0;
  hasDrawnSignature = false;

  // Envío "firmar y enviar" desde el paso 4
  sending = false;                  // flujo firmar+enviar en curso

  // ViewChild setter del canvas (se monta/desmonta al cambiar de pestaña o abrir el modal)
  @ViewChild('respSignCanvas') set respSignCanvasRef(ref: ElementRef<HTMLCanvasElement> | undefined) {
    if (ref && ref.nativeElement !== this.signCanvasEl) {
      this.signCanvasEl = ref.nativeElement;
      this.initSignCanvas();
    } else if (!ref) {
      this.signCanvasEl = undefined;
      this.signCtx = null;
    }
  }
  readonly steps: { num: number; title: string; subtitle: string }[] = [
    { num: 1, title: 'Datos', subtitle: 'Negocio, fechas y notas' },
    { num: 2, title: 'Ítems', subtitle: 'Productos y servicios' },
    { num: 3, title: 'Totales', subtitle: 'Impuestos y condiciones' },
    { num: 4, title: 'Revisión', subtitle: 'Vista previa y envío' }
  ];

  // Controles del FormGroup que se validan al salir de cada paso (validación incremental).
  // Los ítems (paso 2) y "al menos un ítem" se validan aparte (FormArray + regla de negocio).
  // Paso 4 (Revisión y Envío) no valida controles propios: solo persiste el resultado de 1–3.
  private readonly stepControls: Record<number, string[]> = {
    1: ['dealId', 'idContactPrimary', 'datetimeIssued', 'datetimeExpires', 'notes', 'openingMessage', 'internalNotes', 'versionNotes', 'priceListId', 'supervisorUserId'],
    2: [],
    3: ['globalDiscount', 'taxRate', 'taxLabel', 'termsConditions', 'requiresApproval',
        'creditTermDays', 'advancePercent', 'interestRatePercent', 'deliveryTime', 'warranty', 'deliveryPlace', 'exchangeRate'],
    4: []
  };

  // (Incidente 18) Validador de la fecha de EMISIÓN: no puede ser anterior a hoy − 3 días.
  // Excepción: si en EDIT el valor no cambió respecto al guardado, no se valida (cotizaciones antiguas).
  private readonly issuedNotTooOldValidator = (control: AbstractControl): ValidationErrors | null => {
    const v = control.value;
    if (!v) return null;
    if (this.isEditing && this.originalIssued && v === this.originalIssued) return null;
    return v < this.minIssuedDate ? { issuedTooOld: true } : null;
  };

  // (Incidente 18) Validador de la fecha de VALIDEZ: debe ser igual o posterior a hoy.
  //
  // Con la MISMA exención que `issuedNotTooOldValidator` de arriba, que sí la tenía: al editar,
  // el valor que vino guardado pasa siempre. Sin ella, cualquier cotización ya VENCIDA —que por
  // definición tiene la validez en el pasado— quedaba con el formulario inválido, y no se podía
  // ni corregir una nota sin extenderle antes la vigencia. Una fecha pasada NUEVA sigue
  // rechazándose igual que antes.
  private readonly expiresNotPastValidator = (control: AbstractControl): ValidationErrors | null => {
    const v = control.value;
    if (!v) return null;
    if (this.isEditing && this.originalExpires && v === this.originalExpires) return null;
    return v < this.today ? { expiresPast: true } : null;
  };

  constructor(
    private fb: FormBuilder,
    private quoteApi: QuoteApiService,
    private quoteStatusApi: QuoteStatusApiService,
    private quoteApprovalApi: QuoteApprovalApiService,
    private quoteClauseApi: QuoteClauseApiService,
    private priceListApi: PriceListApiService,
    private discountRuleApi: DiscountRuleApiService,
    private dealApi: DealApiService,
    private dealContactApi: DealContactApiService,
    private contactoApi: ContactoApiService,
    private productoApi: ProductoApiService,
    private variantApi: ProductoVariantApiService,
    private tierPriceApi: ProductoTierPriceApiService,
    private bundleApi: ProductBundleApiService,
    private contentTemplateApi: QuoteContentTemplateApiService,
    private authService: AuthService,
    private businessApi: BusinessApiService,
    private datetimeService: DatetimeService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService,
    private sanitizer: DomSanitizer,
    private formModal: QuoteFormModalService
  ) {
    this.today = this.datetimeService.utcToLocalDateInput(new Date().toISOString());
    const validUntilDate = new Date();
    validUntilDate.setDate(validUntilDate.getDate() + this.DEFAULT_VALIDITY_DAYS);
    this.defaultValidUntil = this.datetimeService.utcToLocalDateInput(validUntilDate.toISOString());
    // (Incidente 18) Fecha mínima de emisión = hoy − 3 días.
    const minIssued = new Date();
    minIssued.setDate(minIssued.getDate() - 3);
    this.minIssuedDate = this.datetimeService.utcToLocalDateInput(minIssued.toISOString());
    this.originalIssued = this.today;
    this.initForm();
  }

  ngOnInit(): void {
    this.cargarCatalogos();

    // Modo modal: el id/flags llegan por @Input (la lista ya montó el componente).
    if (this.modalMode) {
      this.initFromInputs();
      this.lockBodyScroll();
      return;
    }

    // Modo ruta (deep-link): el id/flag de envío llegan por la URL.
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.quoteId = +id;
      this.openSendEmailAfterLoad = this.route.snapshot.queryParamMap.get('enviar') === '1';
      this.cargarQuote(this.quoteId);
    } else {
      this.openAddItemForm();
    }
    this.lockBodyScroll();
  }

  // Reaccionar a cambios de @Input cuando la lista reutiliza la misma instancia del modal
  // (p.ej. abrir "Nueva", cerrar y luego "Editar"): recarga el estado del wizard.
  ngOnChanges(changes: SimpleChanges): void {
    if (!this.modalMode) return;
    // Solo recargar cuando cambian las entradas relevantes después del primer ciclo.
    if ((changes['modalQuoteId'] && !changes['modalQuoteId'].firstChange) ||
        (changes['modalSendEmail'] && !changes['modalSendEmail'].firstChange) ||
        (changes['modalDealId'] && !changes['modalDealId'].firstChange)) {
      this.resetWizardState();
      this.initFromInputs();
    }
  }

  ngOnDestroy(): void {
    this.unlockBodyScroll();
    this.revokePdf();
  }

  // Inicializa el wizard a partir de los @Input (modo modal).
  private initFromInputs(): void {
    if (this.modalQuoteId != null) {
      this.isEditing = true;
      this.quoteId = this.modalQuoteId;
      this.openSendEmailAfterLoad = this.modalSendEmail;
      this.cargarQuote(this.quoteId);
    } else {
      this.isEditing = false;
      this.quoteId = null;
      // (INC28) Estado inicial "Borrador". Los catálogos pueden ya estar cargados (reutilización de
      // la instancia del modal): en ese caso el .then() de cargarCatalogos no volverá a dispararse.
      this.aplicarEstadoBorradorPorDefecto();
      // Alta desde el detalle/kanban/lista de negocios: preseleccionar el negocio y precargar
      // su contacto primario en el selector editable del paso 1.
      if (this.modalDealId != null) {
        this.form.get('dealId')?.setValue(this.modalDealId);
        this.updatePickerSelections();
        this.precargarContactoPrimarioDelDeal(this.modalDealId);
        // Los catalogos pueden no haber llegado aun; si es asi, la cascada la re-dispara
        // cargarCatalogos() al llamar a aplicarMonedaPorDefecto().
        this.aplicarMonedaDelDeal();
      }
      this.openAddItemForm();
    }
  }

  // Limpia el estado del wizard para reutilizar la instancia del modal con otra cotización.
  private resetWizardState(): void {
    this.currentStep = 1;
    // El modal se reutiliza para otra cotización: la validez cargada de la anterior no puede
    // seguir eximida de la validación.
    this.originalExpires = '';
    this.error = '';
    this.successMessage = '';
    this.reviewDoc = null;
    this.publicLinkUrl = null;
    this.versions = [];
    this.itemSearchTerm = '';
    this.itemTypeFilter = '';
    this.itemCategoryFilter = '';
    this.contactPickerDisplay = '';
    this.contactManuallyChosen = false;
    this.currencyManuallyChosen = false;
    this.exchangeRateManual = false;
    this.tierAppliedItems = {};
    this.itemDiscountRules = {};
    this.addFormDiscountRule = null;
    this.limpiarEstadoItemsExtendido();
    this.revokePdf();
    this.items.clear();
    this.form.reset();
    this.initForm();
  }

  private bodyScrollLocked = false;
  private previousBodyOverflow: string | null = null;
  private lockBodyScroll(): void {
    if (this.bodyScrollLocked) return;
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.bodyScrollLocked = true;
  }
  private unlockBodyScroll(): void {
    if (!this.bodyScrollLocked) return;
    document.body.style.overflow = this.previousBodyOverflow ?? '';
    this.bodyScrollLocked = false;
  }

  // Cerrar el modal con Esc (salvo que haya un sub-modal/envío en curso).
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.showSendEmailModal || this.showResponsibleSignModal || this.showVersionsModal
        || this.showRequestApprovalModal || this.showImportItemsModal) {
      return; // los sub-modales gestionan su propio cierre
    }
    this.cerrarModal();
  }

  // Cierre del wizard. Modo modal: emite (la lista cierra y refresca si corresponde).
  // Modo ruta: navega de vuelta a la lista.
  cerrarModal(): void {
    if (this.saving || this.savingForReview) return;
    this.unlockBodyScroll();
    if (this.modalMode) {
      this.closed.emit();
    } else {
      this.router.navigate(['/cotizaciones']);
    }
  }

  private cargarCatalogos(): void {
    this.loadingCatalogs = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Solo en CREATE necesitamos la moneda por defecto del negocio para preseleccionar currencyId.
    // En EDIT la moneda real la patchea cargarQuote(). La llamada va junto a los catálogos para
    // no bloquear el render del form; si falla, cae al fallback heurístico (PEN / primera).
    const businessPromise = this.isEditing
      ? Promise.resolve(null)
      : this.businessApi.getById(businessId).toPromise().catch(() => null);

    Promise.all([
      this.quoteStatusApi.getAllStatuses().toPromise(),
      this.dealApi.searchDeals({ businessId, page: 0, size: 1000 }).toPromise(),
      this.productoApi.searchProduct({ businessId, isActive: true, page: 0, size: 1000 }).toPromise(),
      this.bundleApi.searchProductBundle({ businessId, isActive: true, page: 0, size: 1000 }).toPromise(),
      this.productoApi.getCurrencies().toPromise(),
      this.quoteClauseApi.getClauses(businessId).toPromise(),
      // Listas de precios y supervisores (V75): fallan en silencio (selectores opcionales).
      // activeOnly=true (Inc 40/51): solo listas activas en el selector del wizard.
      this.priceListApi.searchPriceList({ businessId, activeOnly: true }).toPromise().catch(() => [] as PriceListResponse[]),
      this.quoteApprovalApi.getActiveUsers(businessId).toPromise().catch(() => [] as CrmUserResponse[]),
      businessPromise
    ]).then(([statuses, dealsResponse, productsResponse, bundlesResponse, currenciesResponse, clausesResponse, priceListsResponse, supervisorsResponse, business]) => {
      this.quoteStatuses = statuses || [];
      this.deals = dealsResponse?.content || [];
      this.products = productsResponse?.content || [];
      this.bundles = bundlesResponse?.content || [];
      this.currencies = currenciesResponse || [];
      this.clauses = clausesResponse || [];
      this.priceLists = priceListsResponse || [];
      this.supervisors = supervisorsResponse || [];
      this.businessDefaultCurrencyId = business?.defaultCurrencyId ?? null;
      this.loadingCatalogs = false;
      this.aplicarMonedaPorDefecto();
      this.aplicarEstadoBorradorPorDefecto();
      this.updatePickerSelections();
    }).catch(() => {
      this.loadingCatalogs = false;
      this.error = 'Error al cargar catálogos';
    });
  }

  /**
   * (INC28) CREATE: el estado de una cotización nueva SIEMPRE es "Borrador" — el backend lo fuerza
   * e ignora el quoteStatusId del request. Aquí se alinea el form: se resuelve el estado por NOMBRE
   * (patrón canónico del repo: el backend usa findByName("Borrador"), nunca un id hardcodeado) y se
   * fija en el control, que en creación NO se renderiza como <select> sino como badge de solo lectura.
   *
   * El guard `!isEditing` evita la carrera con cargarQuote(), que en edición patchea el estado real:
   * catálogos y cotización se cargan en paralelo y cualquiera puede terminar primero.
   */
  private aplicarEstadoBorradorPorDefecto(): void {
    if (this.isEditing) return;
    const draft = this.quoteStatuses.find(s => s.name === 'Borrador');
    if (draft) this.form.patchValue({ quoteStatusId: draft.id });
  }

  /** (INC28) Nombre del estado inicial mostrado como badge de solo lectura en creación. */
  get draftStatusName(): string {
    return this.quoteStatuses.find(s => s.name === 'Borrador')?.name || 'Borrador';
  }

  /**
   * CREATE: preselecciona una moneda por defecto para que el documento no quede en "Moneda: —"
   * y el backend pueda derivar currencyLabel. Orden de preferencia:
   *   1. La moneda por defecto del negocio (GET /api/businesses/{id} → defaultCurrencyId), si está
   *      configurada y existe en el catálogo de monedas.
   *   2. Fallback heurístico: PEN (Soles), o la primera moneda disponible.
   * No toca nada en EDIT (la moneda real la patchea cargarQuote()) ni si el usuario ya eligió una.
   */
  private aplicarMonedaPorDefecto(): void {
    if (this.isEditing) return;
    if (this.currencies.length === 0) return;
    const ctrl = this.form.get('currencyId');
    if (!ctrl || ctrl.value != null) return;

    // 0. Moneda del NEGOCIO elegido. Es la que el usuario ve en la ficha del negocio, así que una
    //    cotización que naciera en otra moneda se lee como un error del sistema.
    if (this.aplicarMonedaDelDeal()) return;

    // 1. Moneda por defecto del negocio (si está configurada y presente en el catálogo).
    if (this.businessDefaultCurrencyId != null) {
      const businessCurrency = this.currencies.find(c => c.id === this.businessDefaultCurrencyId);
      if (businessCurrency) {
        ctrl.setValue(businessCurrency.id);
        return;
      }
    }

    // 2. Fallback heurístico: PEN o la primera disponible.
    const code = this.DEFAULT_CURRENCY_CODE.toUpperCase();
    const preferred = this.currencies.find(c => (c.code || '').toUpperCase() === code);
    const defaultCurrency = preferred ?? this.currencies[0];
    ctrl.setValue(defaultCurrency.id);
  }

  /**
   * Cascada de autocompletado del negocio → moneda de la cotización.
   *
   * El negocio guarda su moneda en `prefix` (código ISO o símbolo, p.ej. "PEN" / "S/"); la ficha del
   * negocio la muestra como badge. Antes la cotización ignoraba ese dato y siempre nacía con la
   * moneda base del negocio (o el fallback PEN), así que un negocio en PEN abría una cotización en
   * USD sin avisar. Devuelve true si consiguió fijar la moneda.
   *
   * No pisa una elección manual (`currencyManuallyChosen`) ni la moneda ya persistida en EDIT.
   */
  private aplicarMonedaDelDeal(): boolean {
    if (this.isEditing || this.currencyManuallyChosen) return false;
    const dealId = this.form.get('dealId')?.value as number | null;
    if (dealId == null || this.currencies.length === 0) return false;

    const prefix = (this.deals.find(d => d.id === dealId)?.prefix || '').trim().toUpperCase();
    if (!prefix) return false;

    const match = this.currencies.find(c => (c.code || '').toUpperCase() === prefix)
      ?? this.currencies.find(c => (c.prefix || '').toUpperCase() === prefix);
    if (!match) return false;

    this.form.get('currencyId')?.setValue(match.id);
    return true;
  }

  /**
   * El usuario tocó el selector de moneda: a partir de aquí la cascada del negocio deja de pisarlo.
   * (El re-cálculo del tipo de cambio lo hace onCurrencyChangeResetExchangeRate.)
   */
  onCurrencyManuallyChanged(): void {
    this.currencyManuallyChosen = true;
    this.onCurrencyChangeResetExchangeRate();
  }

  /** true cuando el usuario eligió la moneda a mano: la cascada del negocio ya no la reemplaza. */
  private currencyManuallyChosen = false;

  // Searchable deal source for the entity-picker (server-side search + Enter-for-all + infinite scroll).
  searchDeals = (term: string, page: number): Observable<PickerSearchPage> => {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    return this.dealApi.searchDeals({
      businessId,
      searchName: term?.trim() || undefined,
      page,
      size: 20
    }).pipe(
      map((res) => ({
        items: (res.content || []).map((d) => ({ id: d.id as number, label: d.name })),
        totalPages: res.totalPages
      }))
    );
  };

  onDealPicked(opt: PickerOption | null): void {
    const ctrl = this.form.get('dealId');
    const previousDealId = ctrl?.value as number | null;
    this.dealPickerSelected = opt;
    ctrl?.setValue(opt?.id ?? null);
    ctrl?.markAsTouched();

    const newDealId = opt?.id ?? null;
    // Cambió el negocio → reemplazar el contacto por el primario del nuevo negocio (resetea la
    // elección manual previa). Si se limpió el negocio, vaciar el selector de contacto.
    if (newDealId != null && newDealId !== previousDealId) {
      this.contactManuallyChosen = false;
      this.precargarContactoPrimarioDelDeal(newDealId);
      // Cascada del negocio: la moneda del negocio manda salvo que el usuario ya eligiera otra.
      // Se fuerza el reemplazo (no solo cuando el control esta vacio) porque cambiar de negocio
      // invalida la moneda heredada del anterior.
      if (!this.isEditing && !this.currencyManuallyChosen) {
        this.form.get('currencyId')?.setValue(null);
        this.aplicarMonedaPorDefecto();
        this.onCurrencyChangeResetExchangeRate();
      }
    } else if (newDealId == null) {
      this.contactManuallyChosen = false;
      this.contactPickerDisplay = '';
      this.form.get('idContactPrimary')?.setValue(null);
      this.dealClientInfo = null;
    }
  }

  // ------------------------------------------------------------
  // Selector de cliente/contacto (idContactPrimary) — Paso 1
  // ------------------------------------------------------------

  // Búsqueda server-side de contactos por nombre/correo (searchTerm). Arrow function para conservar `this`.
  searchContacts = (term: string, page: number): Observable<EntitySelectPage<Contact>> => {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    return this.contactoApi.searchContacts({ businessId, searchTerm: term?.trim() || undefined, page, size: 10 })
      .pipe(map((r) => ({ items: r.content, totalPages: r.totalPages })));
  };
  contactLabel = (c: Contact) => `${c.fullname}${c.lastname ? ' ' + c.lastname : ''}`.trim();
  contactSublabel = (c: Contact) => c.mail ?? '';

  // El usuario eligió/limpió un contacto a mano: marcar para no pisarlo al re-disparar la precarga.
  // Recibe el contacto completo (output `selected`) para mantener sincronizado el texto del resumen.
  onContactPicked(contact: Contact | null): void {
    this.contactManuallyChosen = true;
    this.contactPickerDisplay = contact ? this.contactLabel(contact) : '';
    this.form.get('idContactPrimary')?.setValue(contact?.id ?? null);
    // (Incidente 13) Reflejar el contacto elegido en el bloque "Información del Cliente".
    this.dealClientInfo = contact
      ? { name: this.contactLabel(contact), email: contact.mail || undefined, phone: contact.phone || undefined }
      : null;
  }

  // (Incidente 13) Empresa del cliente: se resuelve del catálogo de negocios por el dealId actual.
  get dealClientCompany(): string {
    const dealId = this.form.get('dealId')?.value as number | null;
    if (dealId == null) return '';
    return this.deals.find(d => d.id === dealId)?.companyName || '';
  }

  // Precarga el contacto primario del negocio en el selector (editable). No pisa una elección manual.
  // Toma el contacto con isPrimary=true o, en su defecto, el primero que devuelve el backend.
  private precargarContactoPrimarioDelDeal(dealId: number): void {
    if (this.contactManuallyChosen) return;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.dealContactApi.getByDeal(dealId, businessId).subscribe({
      next: (contacts) => {
        if (this.contactManuallyChosen) return;
        const primary = contacts.find(c => c.isPrimary) ?? contacts[0];
        if (!primary) {
          this.contactPickerDisplay = '';
          this.form.get('idContactPrimary')?.setValue(null);
          this.dealClientInfo = null;
          return;
        }
        const name = [primary.contactFullname, primary.contactLastname].filter(Boolean).join(' ').trim();
        this.contactPickerDisplay = name || primary.contactMail || `Contacto #${primary.idContact}`;
        this.form.get('idContactPrimary')?.setValue(primary.idContact);
        // (Incidente 13) Mostrar los datos del cliente en el editor (nombre + correo del contacto principal).
        this.dealClientInfo = {
          name: name || primary.contactMail || `Contacto #${primary.idContact}`,
          email: primary.contactMail || undefined
        };
        // Enriquecer con el teléfono del contacto completo (best-effort; no bloquea el flujo).
        this.contactoApi.getContactById(primary.idContact).subscribe({
          next: (c) => {
            if (this.dealClientInfo) {
              this.dealClientInfo = {
                ...this.dealClientInfo,
                email: this.dealClientInfo.email || c.mail || undefined,
                phone: c.phone || undefined
              };
            }
          },
          error: () => { /* sin teléfono: se muestra lo disponible */ }
        });
      },
      // Sin contactos del negocio: el selector queda vacío y editable.
      error: () => { /* noop */ }
    });
  }

  // Rebuild the deal picker chip from the current form value + loaded catalog.
  // Called from both cargarCatalogos and cargarQuote so the last one to finish has all data.
  private updatePickerSelections(): void {
    const dealId = this.form.get('dealId')?.value as number | null;
    if (dealId != null) {
      const deal = this.deals.find((d) => d.id === dealId);
      this.dealPickerSelected = deal
        ? { id: deal.id as number, label: deal.name }
        : { id: dealId, label: `Negocio #${dealId}` };
    } else {
      this.dealPickerSelected = null;
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      dealId: [null, Validators.required],
      // Cliente/contacto principal de la cotización (opcional). Se precarga con el contacto primario
      // del negocio elegido, pero queda editable (el usuario puede buscar y elegir otro contacto).
      idContactPrimary: [null],
      quoteStatusId: [null],
      // CREATE: por defecto hoy + 30 días. En EDIT cargarQuote() lo sobreescribe con el valor real.
      // (Incidente 18) Validadores de fecha: validez ≥ hoy, emisión ≥ hoy − 3 días.
      datetimeExpires: [this.defaultValidUntil, [this.expiresNotPastValidator]],
      datetimeIssued: [this.today, [this.issuedNotTooOldValidator]],
      notes: ['', Validators.maxLength(2000)],
      openingMessage: ['', Validators.maxLength(2000)],
      internalNotes: ['', Validators.maxLength(5000)],
      termsConditions: ['', Validators.maxLength(5000)],
      globalDiscount: [0, [globalDiscountValidator]],
      globalDiscountType: ['fixed'],
      taxEnabled: [true],
      taxRate: [0.18, [Validators.min(0), Validators.max(1)]],
      taxLabel: ['IGV', Validators.maxLength(20)],
      requiresApproval: [false],
      currencyId: [null],
      // Lista de precios y supervisor (V75), ambos opcionales.
      priceListId: [null],
      supervisorUserId: [null],
      // Opciones de presentación del documento (V76).
      groupByCategory: [false],
      showItemCodes: [true],
      versionNotes: ['', Validators.maxLength(500)],
      // ---- Condiciones comerciales (V74), todas opcionales ----
      paymentType: [null],
      creditTermDays: [null, [Validators.min(0)]],
      advancePercent: [null, [Validators.min(0), Validators.max(100)]],
      // Tasa de interés / financiamiento (%) — opcional, 0–100; solo relevante en pagos a crédito.
      interestRatePercent: [null, [Validators.min(0), Validators.max(100)]],
      deliveryTime: ['', Validators.maxLength(500)],
      warranty: ['', Validators.maxLength(500)],
      deliveryPlace: ['', Validators.maxLength(500)],
      exchangeRate: [null, [Validators.min(0)]],
      // (V188) Moneda DESTINO del tipo de cambio: se guarda junto a la tasa para que el
      // documento pueda nombrarla ("Total en Dólar Estadounidense (USD): $ 296.80").
      // null = tasa tecleada a mano ("Otro (manual)").
      exchangeRateCurrencyId: [null],
      items: this.fb.array([])
    });
  }

  get items(): FormArray {
    return this.form.get('items') as FormArray;
  }

  createItemFormGroup(item?: QuoteItemRequest): FormGroup {
    return this.fb.group({
      itemOrder: [item?.itemOrder ?? null],
      itemName: [item?.itemName || '', [Validators.required, Validators.maxLength(200)]],
      description: [item?.description || '', Validators.maxLength(2000)],
      quantity: [item?.quantity || 1, [Validators.required, Validators.min(0.01)]],
      unitPrice: [item?.unitPrice || 0, [Validators.required, Validators.min(0)]],
      discountPercent: [item?.discountPercent || 0, [Validators.min(0), Validators.max(100)]],
      discountAmount: [item?.discountAmount || 0, Validators.min(0)],
      discountType: [item?.discountType || 'percent'],
      unit: [item?.unit || '', Validators.maxLength(50)],
      itemTaxRate: [item?.itemTaxRate ?? null],
      itemCode: [item?.itemCode || '', Validators.maxLength(100)],
      category: [item?.category || '', Validators.maxLength(150)],
      productId: [item?.productId || null],
      itemType: [item?.itemType || 'custom'],
      bundleId: [item?.bundleId || null],
      // (INC33) Variante elegida. variantName/variantSku son SOLO de presentación: se muestran en
      // la fila y buildQuoteRequest los descarta (no forman parte de QuoteItemRequest).
      variantId: [item?.variantId ?? null],
      variantName: [null as string | null],
      variantSku: [null as string | null],
      // (INC32) Regla de descuento aplicada — trazabilidad; el monto efectivo va en discountAmount.
      discountRuleId: [item?.discountRuleId ?? null]
    });
  }

  addItem(): void {
    this.items.push(this.createItemFormGroup());
  }

  removeItem(index: number): void {
    if (this.items.length > 0) {
      this.items.removeAt(index);
      // Shift expandedItems: remove the deleted index, shift all > index down by 1
      const newExpanded = new Set<number>();
      this.expandedItems.forEach(i => {
        if (i < index) {
          newExpanded.add(i);
        } else if (i > index) {
          newExpanded.add(i - 1);
        }
        // i === index is deleted, so skip
      });
      this.expandedItems = newExpanded;

      // Shift itemProductSearchTerms and itemBundleSearchTerms
      const newProductTerms: Record<number, string> = {};
      const newBundleTerms: Record<number, string> = {};
      const newShowProductDropdown: Record<number, boolean> = {};
      const newShowBundleDropdown: Record<number, boolean> = {};
      Object.keys(this.itemProductSearchTerms).forEach(k => {
        const ki = +k;
        if (ki < index) {
          newProductTerms[ki] = this.itemProductSearchTerms[ki];
        } else if (ki > index) {
          newProductTerms[ki - 1] = this.itemProductSearchTerms[ki];
        }
      });
      Object.keys(this.itemBundleSearchTerms).forEach(k => {
        const ki = +k;
        if (ki < index) {
          newBundleTerms[ki] = this.itemBundleSearchTerms[ki];
        } else if (ki > index) {
          newBundleTerms[ki - 1] = this.itemBundleSearchTerms[ki];
        }
      });
      Object.keys(this.showItemProductDropdown).forEach(k => {
        const ki = +k;
        if (ki < index) {
          newShowProductDropdown[ki] = this.showItemProductDropdown[ki];
        } else if (ki > index) {
          newShowProductDropdown[ki - 1] = this.showItemProductDropdown[ki];
        }
      });
      Object.keys(this.showItemBundleDropdown).forEach(k => {
        const ki = +k;
        if (ki < index) {
          newShowBundleDropdown[ki] = this.showItemBundleDropdown[ki];
        } else if (ki > index) {
          newShowBundleDropdown[ki - 1] = this.showItemBundleDropdown[ki];
        }
      });
      this.itemProductSearchTerms = newProductTerms;
      this.itemBundleSearchTerms = newBundleTerms;
      this.showItemProductDropdown = newShowProductDropdown;
      this.showItemBundleDropdown = newShowBundleDropdown;

      const newTierApplied: Record<number, boolean> = {};
      Object.keys(this.tierAppliedItems).forEach(k => {
        const ki = +k;
        if (ki < index) {
          newTierApplied[ki] = this.tierAppliedItems[ki];
        } else if (ki > index) {
          newTierApplied[ki - 1] = this.tierAppliedItems[ki];
        }
      });
      this.tierAppliedItems = newTierApplied;

      const newDiscountRules: Record<number, ResolveDiscountResponse> = {};
      Object.keys(this.itemDiscountRules).forEach(k => {
        const ki = +k;
        if (ki < index) {
          newDiscountRules[ki] = this.itemDiscountRules[ki];
        } else if (ki > index) {
          newDiscountRules[ki - 1] = this.itemDiscountRules[ki];
        }
      });
      this.itemDiscountRules = newDiscountRules;

      // Estado indexado de variantes (INC33) y del control explícito de descuentos (INC32).
      this.itemVariants = this.shiftRecordAfterRemove(this.itemVariants, index);
      this.variantPriceApplied = this.shiftRecordAfterRemove(this.variantPriceApplied, index);
      this.itemDiscountChoice = this.shiftRecordAfterRemove(this.itemDiscountChoice, index);
      this.itemApplicableRules = this.shiftRecordAfterRemove(this.itemApplicableRules, index);
      this.itemCouponCode = this.shiftRecordAfterRemove(this.itemCouponCode, index);
      this.itemCouponError = this.shiftRecordAfterRemove(this.itemCouponError, index);
      this.itemCouponResult = this.shiftRecordAfterRemove(this.itemCouponResult, index);
      this.applyingCoupon = this.shiftRecordAfterRemove(this.applyingCoupon, index);
    }
  }

  /** Reindexa un Record indexado por posición tras eliminar la fila `index` (la clave se descarta). */
  private shiftRecordAfterRemove<T>(record: Record<number, T>, index: number): Record<number, T> {
    const out: Record<number, T> = {};
    Object.keys(record).forEach(k => {
      const ki = +k;
      if (ki < index) out[ki] = record[ki];
      else if (ki > index) out[ki - 1] = record[ki];
    });
    return out;
  }

  // Drag & drop de ítems (#16): reordenar el FormArray y reasignar itemOrder secuencial (0..n).
  // El backend persiste item_order y devuelve los ítems ordenados. Sin toast (regla de drag-drop).
  dropItem(event: CdkDragDrop<unknown>): void {
    // Con filtro activo la lista mostrada es parcial: reordenar índices visibles desalinearía
    // el FormArray real. El drag se deshabilita en el template; este guard es defensivo.
    if (this.hasItemFilter) return;
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(this.items.controls, event.previousIndex, event.currentIndex);
    this.items.updateValueAndValidity();

    // itemOrder secuencial según la nueva posición
    this.items.controls.forEach((ctrl, idx) => {
      ctrl.get('itemOrder')?.setValue(idx, { emitEvent: false });
    });

    // Remapear el estado de UI indexado (expandidos, términos de búsqueda, dropdowns)
    const from = event.previousIndex;
    const to = event.currentIndex;
    const newExpanded = new Set<number>();
    this.expandedItems.forEach(i => newExpanded.add(this.remapIndexAfterMove(i, from, to)));
    this.expandedItems = newExpanded;
    this.itemProductSearchTerms = this.remapRecordAfterMove(this.itemProductSearchTerms, from, to);
    this.itemBundleSearchTerms = this.remapRecordAfterMove(this.itemBundleSearchTerms, from, to);
    this.showItemProductDropdown = this.remapRecordAfterMove(this.showItemProductDropdown, from, to);
    this.showItemBundleDropdown = this.remapRecordAfterMove(this.showItemBundleDropdown, from, to);
    this.tierAppliedItems = this.remapRecordAfterMove(this.tierAppliedItems, from, to);
    this.itemDiscountRules = this.remapRecordAfterMove(this.itemDiscountRules, from, to);
    this.itemVariants = this.remapRecordAfterMove(this.itemVariants, from, to);
    this.variantPriceApplied = this.remapRecordAfterMove(this.variantPriceApplied, from, to);
    this.itemDiscountChoice = this.remapRecordAfterMove(this.itemDiscountChoice, from, to);
    this.itemApplicableRules = this.remapRecordAfterMove(this.itemApplicableRules, from, to);
    this.itemCouponCode = this.remapRecordAfterMove(this.itemCouponCode, from, to);
    this.itemCouponError = this.remapRecordAfterMove(this.itemCouponError, from, to);
    this.itemCouponResult = this.remapRecordAfterMove(this.itemCouponResult, from, to);
    this.applyingCoupon = this.remapRecordAfterMove(this.applyingCoupon, from, to);
  }

  /** Nueva posición de un índice después de mover el elemento `from` a la posición `to`. */
  private remapIndexAfterMove(i: number, from: number, to: number): number {
    if (i === from) return to;
    if (from < to && i > from && i <= to) return i - 1;
    if (to < from && i >= to && i < from) return i + 1;
    return i;
  }

  private remapRecordAfterMove<T>(record: Record<number, T>, from: number, to: number): Record<number, T> {
    const result: Record<number, T> = {};
    Object.keys(record).forEach(k => {
      const ki = +k;
      result[this.remapIndexAfterMove(ki, from, to)] = record[ki];
    });
    return result;
  }

  calculateItemSubtotal(index: number): number {
    const item = this.items.at(index).value;
    const quantity = item.quantity || 0;
    const unitPrice = item.unitPrice || 0;
    const discountAmount = item.discountAmount || 0;
    const discountPercent = item.discountPercent || 0;

    const subtotal = quantity * unitPrice;
    const discountByPercent = subtotal * (discountPercent / 100);
    return Math.max(0, subtotal - discountByPercent - discountAmount);
  }

  calculateSubtotal(): number {
    let total = 0;
    for (let i = 0; i < this.items.length; i++) {
      total += this.calculateItemSubtotal(i);
    }
    return total;
  }

  calculateTax(): number {
    const taxEnabled = this.form.get('taxEnabled')?.value ?? true;
    if (!taxEnabled) return 0;
    const taxRate = this.form.get('taxRate')?.value ?? 0.18;
    return this.calculateSubtotal() * taxRate;
  }

  get effectiveTaxLabel(): string {
    return this.form.get('taxLabel')?.value || 'IGV';
  }

  get effectiveTaxRate(): number {
    return this.form.get('taxRate')?.value ?? 0.18;
  }

  calculateGlobalDiscountAmount(): number {
    const globalDiscount = this.form.get('globalDiscount')?.value || 0;
    const globalDiscountType = this.form.get('globalDiscountType')?.value || 'fixed';
    if (globalDiscountType === 'percent') {
      return this.calculateSubtotal() * (globalDiscount / 100);
    }
    return globalDiscount;
  }

  calculateTotal(): number {
    const subtotal = this.calculateSubtotal();
    const tax = this.calculateTax();
    const discountAmount = this.calculateGlobalDiscountAmount();
    return Math.max(0, subtotal + tax - discountAmount);
  }

  get currencySymbol(): string {
    // Prefer live selection from currencies list, fall back to saved quote value
    const selectedId = this.form.get('currencyId')?.value;
    if (selectedId && this.currencies.length > 0) {
      const c = this.currencies.find(c => c.id === selectedId);
      if (c) return c.prefix || c.code;
    }
    return this.currentQuote?.currencySymbol || 'S/';
  }

  // Etiqueta legible de un usuario para el selector de supervisor (V75).
  supervisorLabel(user: CrmUserResponse): string {
    const name = [user.fullname, user.lastname].filter(Boolean).join(' ').trim() || user.mail;
    return user.roleName ? `${name} — ${user.roleName}` : name;
  }

  // Probabilidad de cierre (READ-ONLY del deal, V75). null si no aplica.
  get dealProbability(): number | null {
    return this.currentQuote?.dealProbability ?? null;
  }

  get dateRangeError(): boolean {
    const issued = this.form.get('datetimeIssued')?.value;
    const expires = this.form.get('datetimeExpires')?.value;
    return !!(issued && expires && expires < issued);
  }

  // ------------------------------------------------------------
  // Navegación del asistente por pasos
  // ------------------------------------------------------------

  // ¿El paso dado es válido? Marca y muestra los errores de sus controles si no lo es.
  // Paso 1: además exige rango de fechas correcto. Paso 2: exige ≥1 ítem y que los ítems sean válidos.
  private validateStep(step: number): boolean {
    let valid = true;

    for (const name of (this.stepControls[step] || [])) {
      const control = this.form.get(name);
      if (control) {
        control.markAsTouched();
        if (control.invalid) valid = false;
      }
    }

    if (step === 1 && this.dateRangeError) {
      this.error = 'La fecha "Válido Hasta" debe ser posterior a la Fecha de Emisión';
      valid = false;
    }

    if (step === 2) {
      if (this.items.length === 0) {
        this.error = 'Debe agregar al menos un ítem a la cotización';
        valid = false;
      } else {
        this.items.controls.forEach(c => c.markAllAsTouched());
        if (this.items.invalid) valid = false;
      }
    }

    if (valid) this.error = '';
    return valid;
  }

  // ¿Un paso ya quedó válido? (para el check verde del stepper). No marca controles (lectura pura).
  isStepValid(step: number): boolean {
    const controlsOk = (this.stepControls[step] || []).every(name => {
      const c = this.form.get(name);
      return !c || c.valid;
    });
    if (step === 1) return controlsOk && !this.dateRangeError;
    if (step === 2) return this.items.length > 0 && this.items.valid;
    return controlsOk;
  }

  // Motivo legible por el que el botón Siguiente/Guardar del paso actual no puede avanzar.
  // Cadena vacía = el paso está listo. Se usa como tooltip del botón (no deshabilita el click:
  // al pulsar, validateStep() marca y resalta el campo con error y muestra el mensaje en el banner).
  get currentStepBlockReason(): string {
    switch (this.currentStep) {
      case 1:
        if (!this.form.get('dealId')?.value) return 'Selecciona un negocio para continuar';
        if (this.dateRangeError) return 'La fecha "Válido hasta" debe ser posterior a la de emisión';
        return '';
      case 2:
        if (this.items.length === 0) return 'Agrega al menos un ítem para continuar';
        if (this.items.invalid) return 'Revisa los ítems: hay datos incompletos o inválidos';
        return '';
      case 3:
        if (!this.isStepValid(3)) return 'Revisa las condiciones: hay valores fuera de rango';
        return '';
      default:
        return '';
    }
  }

  nextStep(): void {
    if (!this.validateStep(this.currentStep)) return;
    if (this.currentStep < this.totalSteps) {
      const target = this.currentStep + 1;
      // Entrar al paso 4 (Revisión y Envío) persiste primero la cotización y carga la vista previa.
      if (target === this.totalSteps) {
        this.entrarRevision();
        return;
      }
      this.currentStep = target;
    }
  }

  prevStep(): void {
    this.error = '';
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  // Click en el stepper. En EDIT permite saltar a cualquier paso. En CREATE solo permite ir hacia
  // atrás libremente o avanzar de a uno validando el paso actual.
  goToStep(step: number): void {
    if (step === this.currentStep) return;
    if (step < this.currentStep) {
      this.error = '';
      this.currentStep = step;
      return;
    }
    if (this.isEditing) {
      // Edición: salto libre hacia adelante validando todos los pasos intermedios.
      for (let s = this.currentStep; s < step; s++) {
        if (!this.validateStep(s)) {
          this.currentStep = s;
          return;
        }
      }
      // Llegar al paso 4 siempre persiste primero y carga la vista previa.
      if (step === this.totalSteps) {
        this.entrarRevision();
        return;
      }
      this.currentStep = step;
      return;
    }
    // Creación: avanzar de a uno (solo si el siguiente es el inmediato).
    if (step === this.currentStep + 1) {
      this.nextStep();
    }
  }

  cargarQuote(id: number): void {
    this.loading = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.quoteApi.getQuoteById(id, businessId).subscribe({
      next: (quote: QuoteResponse) => {
        this.currentQuote = quote;
        this.cargarAprobacionPendiente(quote);

        // Recuperar el enlace público revelado en esta sesión (publish/rotate/send-email)
        // para que "Copiar enlace de cliente" siga disponible al reabrir el form.
        if (!this.publicLinkUrl && quote.hasPublicLink) {
          this.publicLinkUrl = sessionStorage.getItem(`quote_public_link_${id}`);
        }

        const expiresValue = quote.datetimeExpires
          ? this.datetimeService.utcToLocalDateInput(quote.datetimeExpires)
          : (quote.validUntil ? quote.validUntil.substring(0, 10) : '');

        const issuedValue = quote.datetimeIssued
          ? this.datetimeService.utcToLocalDateInput(quote.datetimeIssued)
          : '';
        // (Incidente 18) Recordar la emisión cargada: en EDIT solo se reenvía si el usuario la cambia.
        this.originalIssued = issuedValue;
        this.originalExpires = expiresValue;

        this.form.patchValue({
          dealId: quote.dealId,
          idContactPrimary: quote.idContactPrimary ?? null,
          quoteStatusId: quote.quoteStatusId,
          datetimeExpires: expiresValue,
          datetimeIssued: issuedValue,
          notes: quote.notes,
          openingMessage: quote.openingMessage ?? '',
          internalNotes: quote.internalNotes ?? '',
          termsConditions: quote.termsConditions,
          globalDiscount: quote.globalDiscount ?? quote.discount ?? 0,
          globalDiscountType: quote.globalDiscountType || 'fixed',
          taxEnabled: quote.taxEnabled ?? true,
          taxRate: quote.taxRate ?? 0.18,
          taxLabel: quote.taxLabel || 'IGV',
          requiresApproval: quote.requiresApproval ?? false,
          currencyId: quote.currencyId ?? null,
          priceListId: quote.priceListId ?? null,
          supervisorUserId: quote.supervisorUserId ?? null,
          // Opciones de presentación del documento (V76).
          groupByCategory: quote.groupByCategory ?? false,
          showItemCodes: quote.showItemCodes ?? true,
          versionNotes: quote.versionNotes || '',
          // Condiciones comerciales (V74)
          paymentType: quote.paymentType ?? null,
          creditTermDays: quote.creditTermDays ?? null,
          advancePercent: quote.advancePercent ?? null,
          interestRatePercent: quote.interestRatePercent ?? null,
          deliveryTime: quote.deliveryTime ?? '',
          warranty: quote.warranty ?? '',
          deliveryPlace: quote.deliveryPlace ?? '',
          exchangeRate: quote.exchangeRate ?? null,
          exchangeRateCurrencyId: quote.exchangeRateCurrencyId ?? null
        });

        // Sync searchable picker chips now that the quote (deal/status) is loaded.
        this.updatePickerSelections();

        // Editar: mostrar el contacto actual de la cotización en el selector (no autoprecargar del
        // negocio). Marcar como elección fija para que no la pise la precarga.
        if (quote.idContactPrimary != null) {
          const name = quote.contactFullName?.trim();
          this.contactPickerDisplay = name || quote.contactEmail || `Contacto #${quote.idContactPrimary}`;
          this.contactManuallyChosen = true;
          // (Incidente 13) Datos del cliente visibles también en edición.
          this.dealClientInfo = {
            name: name || quote.contactEmail || `Contacto #${quote.idContactPrimary}`,
            email: quote.contactEmail || undefined,
            phone: quote.contactPhone || undefined
          };
        } else {
          this.contactPickerDisplay = '';
          this.contactManuallyChosen = false;
          this.dealClientInfo = null;
        }

        // Auto-expand text sections if they have content
        this.showNotes = !!quote.notes;
        this.showOpeningMessage = !!quote.openingMessage;
        this.showInternalNotes = !!quote.internalNotes;
        this.showTerms = !!quote.termsConditions;
        this.showVersionNotes = !!quote.versionNotes;

        // Clear and populate items
        this.items.clear();
        this.expandedItems = new Set<number>();
        this.itemProductSearchTerms = {};
        this.itemBundleSearchTerms = {};
        this.showItemProductDropdown = {};
        this.showItemBundleDropdown = {};
        this.tierAppliedItems = {};
        this.itemDiscountRules = {};
        this.limpiarEstadoItemsExtendido();

        if (quote.items && quote.items.length > 0) {
          quote.items.forEach(item => {
            const group = this.createItemFormGroup({
              itemOrder: item.itemOrder,
              itemName: item.itemName,
              description: item.description,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              discountPercent: item.discountPercent,
              discountAmount: item.discountAmount,
              discountType: (item.discountType as 'percent' | 'fixed') || 'percent',
              unit: item.unit || '',
              // itemTaxRate viene como fracción (0.18); el input lo muestra en % (18).
              itemTaxRate: item.itemTaxRate != null ? item.itemTaxRate * 100 : undefined,
              itemCode: item.itemCode || '',
              category: item.category || '',
              productId: item.productId,
              itemType: (item.itemType as QuoteItemRequest['itemType']) || 'custom',
              bundleId: item.bundleId,
              // (INC33/INC32) Variante y regla congeladas en la línea.
              variantId: item.variantId ?? null,
              discountRuleId: item.discountRuleId ?? null
            });
            // Nombre/SKU de la variante: solo presentación, resueltos por el backend en la respuesta.
            group.patchValue(
              { variantName: item.variantName ?? null, variantSku: item.variantSku ?? null },
              { emitEvent: false }
            );
            this.items.push(group);
          });
        } else {
          this.addItem();
        }

        // Cláusulas seleccionadas de la cotización (V72).
        this.selectedClauseIds = new Set<number>((quote.clauses || []).map(c => c.id));
        this.showClauses = this.selectedClauseIds.size > 0;

        this.loading = false;

        // Navegación desde la lista con ?enviar=1: abrir el modal de envío una sola vez.
        if (this.openSendEmailAfterLoad) {
          this.openSendEmailAfterLoad = false;
          this.abrirEnviarCorreo();
        }
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la cotización';
        this.loading = false;
      }
    });
  }

  // Construye el QuoteRequest desde el estado del form (reutilizado por guardar() y el paso 4).
  // Incluye condiciones comerciales (V74): paymentType + creditTermDays solo si es crédito.
  private buildQuoteRequest(): QuoteRequest {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    const formValue = this.form.value;

    const paymentType: QuotePaymentType | null = formValue.paymentType || null;
    const num = (v: unknown): number | null =>
      v === null || v === undefined || v === '' ? null : Number(v);

    return {
      dealId: formValue.dealId,
      quoteStatusId: formValue.quoteStatusId,
      // (Incidente 19) Fechas date-only fijadas a mediodía UTC: ningún offset de zona cambia el día.
      datetimeExpires: formValue.datetimeExpires ? this.dateOnlyToUtcIso(formValue.datetimeExpires) : undefined,
      // (Incidente 18) En EDIT, solo reenviar la emisión si el usuario la cambió (cotizaciones antiguas).
      datetimeIssued: this.shouldSendIssued(formValue.datetimeIssued) ? this.dateOnlyToUtcIso(formValue.datetimeIssued) : undefined,
      notes: formValue.notes || undefined,
      openingMessage: formValue.openingMessage || undefined,
      internalNotes: formValue.internalNotes || undefined,
      termsConditions: formValue.termsConditions || undefined,
      globalDiscount: formValue.globalDiscount,
      globalDiscountType: formValue.globalDiscountType,
      taxEnabled: formValue.taxEnabled,
      taxRate: formValue.taxRate,
      taxLabel: formValue.taxLabel,
      // Siempre boolean explícito: en el PUT, omitirlo significa "no modificar" en el backend.
      requiresApproval: !!formValue.requiresApproval,
      currencyId: formValue.currencyId || undefined,
      versionNotes: formValue.versionNotes || undefined,
      // Condiciones comerciales (V74). El crédito en días solo aplica si la forma de pago es CREDITO.
      paymentType,
      creditTermDays: paymentType === 'CREDITO' ? num(formValue.creditTermDays) : null,
      advancePercent: num(formValue.advancePercent),
      // Tasa de interés / financiamiento: solo aplica al saldo financiado en pagos a crédito.
      interestRatePercent: paymentType === 'CREDITO' ? num(formValue.interestRatePercent) : null,
      deliveryTime: formValue.deliveryTime || null,
      warranty: formValue.warranty || null,
      deliveryPlace: formValue.deliveryPlace || null,
      exchangeRate: num(formValue.exchangeRate),
      // (V188) La moneda destino viaja pegada a la tasa: null cuando se tecleó a mano.
      exchangeRateCurrencyId: formValue.exchangeRateCurrencyId ?? null,
      // Lista de precios y supervisor (V75): null = sin lista / sin supervisor.
      priceListId: formValue.priceListId ?? null,
      supervisorUserId: formValue.supervisorUserId ?? null,
      // Opciones de presentación del documento (V76). Siempre boolean explícito.
      groupByCategory: !!formValue.groupByCategory,
      showItemCodes: !!formValue.showItemCodes,
      // Cliente/contacto principal: lo toma el selector editable del paso 1 (precargado con el
      // contacto primario del negocio, pero el usuario pudo cambiarlo). Vacío → undefined.
      idContactPrimary: formValue.idContactPrimary ?? undefined,
      // Preservar el resto de datos heredados del negocio al actualizar (no se editan en este form,
      // pero el PUT reemplaza el recurso completo y sin esto se perderían).
      idTemplate: this.currentQuote?.idTemplate ?? undefined,
      // Cláusulas legales seleccionadas (V72): el orden del catálogo activo define el sortOrder.
      clauseIds: this.clauses.filter(c => this.selectedClauseIds.has(c.id)).map(c => c.id),
      businessId,
      // El campo "Impuesto %" por línea se captura en porcentaje (18) pero el backend
      // guarda itemTaxRate como fracción (0.18), igual que taxRate. Convertir aquí.
      items: (formValue.items as ItemFormValue[]).map((it, idx) => {
        const taxPct = num(it.itemTaxRate);
        // category vacío → null para que el backend la autocomplete con la del producto al guardar.
        const cat = typeof it.category === 'string' ? it.category.trim() : it.category;
        // variantName/variantSku son estado de presentación de la fila: NO forman parte del contrato.
        const { variantName: _vn, variantSku: _vs, ...rest } = it;
        // (INC33) El backend valida que variantId solo venga en ítems 'product': cualquier otro tipo
        // responde 400. Un cambio de tipo ya limpia la variante, pero se re-fuerza aquí por seguridad.
        const variantId = it.itemType === 'product' ? (it.variantId ?? null) : null;
        return {
          ...rest,
          itemTaxRate: taxPct === null ? undefined : taxPct / 100,
          category: cat ? cat : null,
          variantId,
          // (INC33) Con variante y precio no tocado por el usuario, unitPrice=0 delega en el backend
          // la resolución del precio efectivo (price_override ?? price + surcharge). Si el usuario
          // editó el precio, se manda tal cual y el backend lo respeta.
          unitPrice: variantId != null && this.variantPriceApplied[idx] ? 0 : it.unitPrice,
          // (INC32) Trazabilidad de la regla: el descuento efectivo ya viaja en discountAmount.
          discountRuleId: it.discountRuleId ?? null
        };
      })
    };
  }

  /**
   * (Incidente 19) Convierte una fecha date-only (YYYY-MM-DD) a ISO fijada a MEDIODÍA UTC, de modo que
   * la conversión a la zona horaria del usuario nunca cruce el límite del día (no hay corrimiento ±1).
   * Si el valor ya trae hora/zona, delega en el servicio (passthrough).
   */
  private dateOnlyToUtcIso(dateStr: string): string {
    if (!dateStr) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return `${dateStr}T12:00:00Z`;
    return this.datetimeService.dateToUtcIso(dateStr);
  }

  /**
   * (Incidente 18) ¿Debe enviarse datetimeIssued en el request? En creación siempre (si hay valor).
   * En edición solo si el usuario lo cambió respecto al cargado, para no disparar el 400 del backend
   * al reenviar la emisión original de una cotización antigua (>3 días).
   */
  private shouldSendIssued(value: string): boolean {
    if (!value) return false;
    if (!this.isEditing) return true;
    return value !== this.originalIssued;
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
    }

    // Validar paso por paso y, si algo falla, llevar al usuario al primer paso con error.
    for (let s = 1; s <= this.totalSteps; s++) {
      if (!this.validateStep(s)) {
        this.currentStep = s;
        return;
      }
    }

    this.saving = true;
    this.error = '';
    this.successMessage = '';

    const quoteRequest = this.buildQuoteRequest();

    if (this.isEditing && this.quoteId) {
      this.quoteApi.updateQuote(this.quoteId, quoteRequest).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Cotización');
          this.finalizarGuardado();
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar la cotización';
          this.toast.error({ title: 'Error', message: err.error?.message || 'Error al actualizar la cotización' });
        }
      });
    } else {
      this.quoteApi.createQuote(quoteRequest).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Cotización');
          this.finalizarGuardado();
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear la cotización';
          this.toast.error({ title: 'Error', message: err.error?.message || 'Error al crear la cotización' });
        }
      });
    }
  }

  // Tras guardar: modo modal → cerrar y avisar a la lista; modo ruta → navegar a la lista.
  private finalizarGuardado(): void {
    this.unlockBodyScroll();
    if (this.modalMode) {
      this.savedQuote.emit();
      this.closed.emit();
    } else {
      this.router.navigate(['/cotizaciones']);
    }
  }

  // ============================================================
  // Paso 4 — Revisión y Envío
  // ============================================================

  /**
   * Entrar al paso 4: persiste la cotización (create si es nueva, update si existe) y luego carga
   * su QuoteResponse para alimentar la vista previa. Si el guardado falla, regresa al primer paso
   * con error (reusa la validación por paso). Tras guardar, el form pasa a modo edición.
   */
  private entrarRevision(): void {
    // Validar todos los pasos anteriores; si algo falla, llevar al primer paso con error.
    for (let s = 1; s < this.totalSteps; s++) {
      if (!this.validateStep(s)) {
        this.currentStep = s;
        return;
      }
    }

    this.savingForReview = true;
    this.error = '';
    this.successMessage = '';
    const request = this.buildQuoteRequest();

    const onError = (err: { error?: { message?: string } }) => {
      this.savingForReview = false;
      const msg = err.error?.message || 'No se pudo guardar la cotización para la revisión';
      this.error = msg;
      this.toast.error({ title: 'Error', message: msg });
      // Regresar al paso 1 para que el usuario corrija (la validación por paso ya pasó en cliente).
      this.currentStep = 1;
    };

    if (this.isEditing && this.quoteId) {
      this.quoteApi.updateQuote(this.quoteId, request).subscribe({
        next: () => this.trasGuardarRevision(this.quoteId!),
        error: onError
      });
    } else {
      this.quoteApi.createQuote(request).subscribe({
        next: (created) => {
          // Pasar a modo edición para poder firmar / enviar.
          this.isEditing = true;
          this.quoteId = created.id;
          // Modo ruta: sincronizar la URL con la cotización recién creada (deep-link consistente).
          // Modo modal: no navegar (el wizard vive como overlay sobre la lista).
          if (!this.modalMode) {
            this.router.navigate(['/cotizaciones/editar', created.id], { replaceUrl: true });
          }
          this.trasGuardarRevision(created.id);
        },
        error: onError
      });
    }
  }

  // Recarga la cotización persistida, construye los datos del documento y entra al paso 4.
  private trasGuardarRevision(id: number): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.quoteApi.getQuoteById(id, businessId).subscribe({
      next: (quote) => {
        this.aplicarQuoteCargada(quote);
        // (Incidente 16) vista previa = documento del cliente → oculta la versión "(vN)".
        this.reviewDoc = quoteDocumentFromResponse(quote, true);
        this.savingForReview = false;
        this.currentStep = this.totalSteps;
        // Cargar el PDF real generado por el backend para la vista previa del paso 4.
        this.loadReviewPdf();
      },
      error: (err) => {
        this.savingForReview = false;
        this.error = err.error?.message || 'No se pudo cargar la vista previa';
        this.toast.error({ title: 'Error', message: this.error });
        this.currentStep = 1;
      }
    });
  }

  // Refresca solo la vista previa (tras firmar): vuelve a leer la cotización y rearma el documento.
  private refrescarRevision(): void {
    if (!this.quoteId) return;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.quoteApi.getQuoteById(this.quoteId, businessId).subscribe({
      next: (quote) => {
        this.currentQuote = quote;
        // (Incidente 16) vista previa = documento del cliente → oculta la versión "(vN)".
        this.reviewDoc = quoteDocumentFromResponse(quote, true);
        // Regenerar el PDF: tras firmar, el documento cambió (aparece la firma del responsable).
        this.loadReviewPdf();
      },
      error: () => { /* la vista previa anterior se conserva */ }
    });
  }

  // ---- Vista previa del PDF real (paso 4) ----

  /**
   * Descarga el PDF de la cotización persistida (GET /api/quotes/{id}/pdf, blob), crea un object URL
   * saneado y lo muestra en un <iframe>. Si la generación falla, marca pdfError para caer a la vista
   * previa HTML (quote-document) como respaldo. El object URL anterior se revoca para no fugar memoria.
   */
  loadReviewPdf(): void {
    if (!this.quoteId) return;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.pdfLoading = true;
    this.pdfError = false;
    this.revokePdf();
    this.quoteApi.downloadPdf(this.quoteId, businessId).subscribe({
      next: (blob) => {
        // Forzar el tipo PDF por si el backend no setea Content-Type en el blob.
        const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
        this.pdfObjectUrl = window.URL.createObjectURL(pdfBlob);
        this.pdfSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfObjectUrl);
        this.pdfLoading = false;
      },
      error: () => {
        // Sin PDF: el template muestra la vista previa HTML (reviewDoc) como respaldo.
        this.pdfLoading = false;
        this.pdfError = true;
      }
    });
  }

  private revokePdf(): void {
    if (this.pdfObjectUrl) {
      window.URL.revokeObjectURL(this.pdfObjectUrl);
      this.pdfObjectUrl = null;
    }
    this.pdfSafeUrl = null;
  }

  /** Aplica al estado del componente una cotización recién cargada (sin recargar el FormArray). */
  private aplicarQuoteCargada(quote: QuoteResponse): void {
    this.currentQuote = quote;
    this.cargarAprobacionPendiente(quote);
    if (!this.publicLinkUrl && quote.hasPublicLink) {
      this.publicLinkUrl = sessionStorage.getItem(`quote_public_link_${quote.id}`);
    }
  }

  /** ¿El responsable ya firmó? (firma APPROVER en el documento). */
  get isResponsibleSigned(): boolean {
    return !!(this.reviewDoc?.approverSignature
      || (this.currentQuote?.signatures || []).some(s => s.signerType === 'APPROVER'));
  }

  /** ¿El cliente ya firmó? (informativo en el paso 4). */
  get isClientSigned(): boolean {
    return !!(this.reviewDoc?.clientSignature
      || (this.currentQuote?.signatures || []).some(s => s.signerType === 'CLIENT'));
  }

  // ---- Firma del responsable ----

  abrirFirmaResponsable(): void {
    if (!this.quoteId) return;
    this.responsibleSignError = '';
    this.signTab = 'registered';
    const u = this.authService.getCurrentUser();
    this.typedSignatureName = u ? [u.fullname, u.lastname].filter(Boolean).join(' ').trim() : '';
    this.uploadedSignature = null;
    this.uploadSignatureError = '';
    this.hasDrawnSignature = false;
    this.showResponsibleSignModal = true;
  }

  cerrarFirmaResponsable(): void {
    if (this.responsibleSigning) return;
    this.showResponsibleSignModal = false;
  }

  setSignTab(tab: 'registered' | 'drawn' | 'typed' | 'upload'): void {
    if (this.signTab === tab) return;
    this.signTab = tab;
    this.responsibleSignError = '';
    if (tab !== 'drawn') this.hasDrawnSignature = false;
  }

  get typedSignaturePreview(): string {
    return this.typedSignatureName.trim() || 'Tu firma';
  }

  // Arma el body de responsible-sign según la pestaña activa. registered → undefined (sin body).
  private buildSignRequest(): ResponsibleSignRequest | undefined | null {
    if (this.signTab === 'registered') return undefined;
    if (this.signTab === 'drawn') {
      if (!this.hasDrawnSignature || !this.signCanvasEl) {
        this.responsibleSignError = 'Dibuja tu firma en el recuadro antes de continuar.';
        return null;
      }
      return { signatureData: this.signCanvasEl.toDataURL('image/png'), signatureType: 'drawn' };
    }
    if (this.signTab === 'typed') {
      const name = this.typedSignatureName.trim();
      if (!name) {
        this.responsibleSignError = 'Escribe tu nombre para usarlo como firma.';
        return null;
      }
      return { signatureData: name, signatureType: 'typed' };
    }
    // upload
    if (!this.uploadedSignature) {
      this.responsibleSignError = 'Sube una imagen de tu firma antes de continuar.';
      return null;
    }
    return { signatureData: this.uploadedSignature, signatureType: 'uploaded' };
  }

  // Confirma la firma del responsable. 422 (sin firma registrada) → toast invitando a dibujar.
  confirmarFirmaResponsable(): void {
    if (!this.quoteId || this.responsibleSigning) return;
    this.responsibleSignError = '';
    const body = this.buildSignRequest();
    if (body === null) return; // error de validación ya seteado
    this.responsibleSigning = true;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.quoteApi.responsibleSign(this.quoteId, businessId, body ?? undefined).subscribe({
      next: () => {
        this.responsibleSigning = false;
        this.showResponsibleSignModal = false;
        this.toast.success({ title: 'Firmado', message: 'Tu firma como responsable se estampó en la cotización.' });
        this.refrescarRevision();
      },
      error: (err) => {
        this.responsibleSigning = false;
        const msg: string = err?.error?.message || 'No se pudo firmar la cotización';
        if (err?.status === 422) {
          // Sin firma en body y el usuario no tiene firma registrada: invitar a dibujar/escribir.
          this.responsibleSignError = msg + ' Dibuja o escribe una firma en este panel.';
          this.toast.error({ title: 'Sin firma registrada', message: msg });
          if (this.signTab === 'registered') this.signTab = 'drawn';
        } else {
          this.responsibleSignError = msg;
          this.toast.error({ title: 'Error', message: msg });
        }
      }
    });
  }

  // ---- Canvas de firma manuscrita (patrón del portal público) ----

  private initSignCanvas(): void {
    if (!this.signCanvasEl) return;
    const dpr = window.devicePixelRatio || 1;
    const width = this.signCanvasEl.clientWidth || 400;
    const height = this.signCanvasEl.clientHeight || 176;
    this.signCanvasEl.width = Math.round(width * dpr);
    this.signCanvasEl.height = Math.round(height * dpr);
    this.signCtx = this.signCanvasEl.getContext('2d');
    if (this.signCtx) {
      this.signCtx.scale(dpr, dpr);
      this.signCtx.strokeStyle = '#1f2937';
      this.signCtx.lineWidth = 2.5;
      this.signCtx.lineCap = 'round';
      this.signCtx.lineJoin = 'round';
    }
    this.hasDrawnSignature = false;
  }

  private signPointerPos(e: PointerEvent): { x: number; y: number } {
    const rect = this.signCanvasEl!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  onSignPointerDown(e: PointerEvent): void {
    if (!this.signCanvasEl || !this.signCtx) return;
    e.preventDefault();
    this.signCanvasEl.setPointerCapture(e.pointerId);
    this.signDrawing = true;
    const { x, y } = this.signPointerPos(e);
    this.signLastX = x;
    this.signLastY = y;
    this.signCtx.beginPath();
    this.signCtx.moveTo(x, y);
    this.signCtx.lineTo(x + 0.1, y + 0.1);
    this.signCtx.stroke();
    this.hasDrawnSignature = true;
  }

  onSignPointerMove(e: PointerEvent): void {
    if (!this.signDrawing || !this.signCtx) return;
    e.preventDefault();
    const { x, y } = this.signPointerPos(e);
    const midX = (this.signLastX + x) / 2;
    const midY = (this.signLastY + y) / 2;
    this.signCtx.beginPath();
    this.signCtx.moveTo(this.signLastX, this.signLastY);
    this.signCtx.quadraticCurveTo(this.signLastX, this.signLastY, midX, midY);
    this.signCtx.lineTo(x, y);
    this.signCtx.stroke();
    this.signLastX = x;
    this.signLastY = y;
  }

  onSignPointerUp(e: PointerEvent): void {
    if (!this.signDrawing) return;
    this.signDrawing = false;
    if (this.signCanvasEl?.hasPointerCapture(e.pointerId)) {
      this.signCanvasEl.releasePointerCapture(e.pointerId);
    }
  }

  limpiarFirmaResponsable(): void {
    if (!this.signCanvasEl || !this.signCtx) return;
    const dpr = window.devicePixelRatio || 1;
    this.signCtx.clearRect(0, 0, this.signCanvasEl.width / dpr, this.signCanvasEl.height / dpr);
    this.hasDrawnSignature = false;
  }

  onResponsibleSignatureFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    this.uploadSignatureError = '';
    if (!file) return;
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      this.uploadSignatureError = 'Solo se permiten imágenes PNG o JPG.';
      input.value = '';
      return;
    }
    if (file.size > 1024 * 1024) {
      this.uploadSignatureError = 'La imagen no debe superar 1 MB.';
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { this.uploadedSignature = reader.result as string; };
    reader.readAsDataURL(file);
  }

  quitarImagenFirmaResponsable(): void {
    this.uploadedSignature = null;
    this.uploadSignatureError = '';
  }

  // ---- Firmar y enviar (acción principal del paso 4) ----

  // Si el responsable aún no firmó, firma con la firma registrada y luego envía; si ya firmó, solo envía.
  // Respeta el bloqueo por aprobación interna (el backend devuelve 422) abriendo el flujo de envío.
  firmarYEnviar(): void {
    if (!this.quoteId || this.sending) return;
    if (this.isPendingApproval || this.isApprovalBlocked) {
      this.toast.error({
        title: 'Envío bloqueado',
        message: 'Esta cotización requiere aprobación interna. Solicita la aprobación antes de enviarla.'
      });
      return;
    }
    if (this.isResponsibleSigned) {
      this.abrirEnviarCorreo();
      return;
    }
    // No firmada aún: intentar firmar con la firma registrada; si no hay, abrir el panel de firma.
    this.sending = true;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.quoteApi.responsibleSign(this.quoteId, businessId, undefined).subscribe({
      next: () => {
        this.sending = false;
        this.toast.success({ title: 'Firmado', message: 'Se usó tu firma registrada.' });
        this.refrescarRevision();
        this.abrirEnviarCorreo();
      },
      error: (err) => {
        this.sending = false;
        const msg: string = err?.error?.message || 'No se pudo firmar la cotización';
        if (err?.status === 422) {
          this.toast.error({ title: 'Sin firma registrada', message: msg + ' Firma manualmente en el panel.' });
          this.abrirFirmaResponsable();
        } else {
          this.toast.error({ title: 'Error', message: msg });
        }
      }
    });
  }

  publicar(): void {
    // Borrador + requiere aprobación interna: el backend devolvería 422.
    if (!this.quoteId || this.isApprovalBlocked) return;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.publishing = true;
    this.error = '';
    this.successMessage = '';

    this.quoteApi.publishQuote(this.quoteId, businessId).subscribe({
      next: (quote) => {
        this.publishing = false;
        // El enlace se revela SOLO en esta respuesta: capturarlo y copiarlo de inmediato.
        this.captureAndCopyLink(quote, 'Cotización publicada. Enlace de cliente copiado al portapapeles.');
        this.cargarQuote(this.quoteId!);
      },
      error: (err) => {
        this.publishing = false;
        this.error = err.error?.message || 'Error al publicar la cotización';
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al publicar la cotización' });
      }
    });
  }

  // Genera un enlace nuevo para una cotización publicada que quedó sin enlace (revocado). No cambia el estado.
  generarEnlace(): void {
    this.applyRotate('Enlace generado y copiado al portapapeles.');
  }

  async rotarEnlace(): Promise<void> {
    const ok = await this.confirmDialog.confirm({
      title: 'Rotar enlace',
      message: 'Se emitirá un enlace nuevo y el anterior dejará de funcionar de inmediato. ¿Continuar?',
      confirmText: 'Rotar enlace'
    });
    if (!ok) return;
    this.applyRotate('Enlace rotado. El anterior ya no funciona. Nuevo enlace copiado al portapapeles.');
  }

  private applyRotate(successMsg: string): void {
    if (!this.quoteId) return;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.rotating = true;
    this.quoteApi.rotateLink(this.quoteId, businessId).subscribe({
      next: (quote) => {
        this.rotating = false;
        this.captureAndCopyLink(quote, successMsg);
        this.cargarQuote(this.quoteId!);
      },
      error: (err) => {
        this.rotating = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo generar el enlace' });
      }
    });
  }

  async revocarEnlace(): Promise<void> {
    if (!this.quoteId) return;
    const ok = await this.confirmDialog.confirm({
      title: 'Revocar enlace',
      message: 'El enlace público dejará de funcionar. Podrás volver a compartir más tarde generando uno nuevo. ¿Continuar?',
      confirmText: 'Revocar enlace',
      variant: 'danger'
    });
    if (!ok) return;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.revoking = true;
    this.quoteApi.revokeLink(this.quoteId, businessId).subscribe({
      next: () => {
        this.revoking = false;
        this.publicLinkUrl = null;
        this.persistPublicLink();
        this.toast.success({ title: 'Enlace revocado', message: 'El enlace público ya no funciona.' });
        this.cargarQuote(this.quoteId!);
      },
      error: (err) => {
        this.revoking = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo revocar el enlace' });
      }
    });
  }

  copiarEnlace(): void {
    if (!this.publicLinkUrl) return;
    this.copyToClipboard(this.publicLinkUrl, 'Enlace de cliente copiado al portapapeles.');
  }

  // Persiste el enlace público en sessionStorage para que la vista de detalle (solo lectura)
  // pueda ofrecer "Copiar enlace público" mientras dure la sesión del navegador.
  private persistPublicLink(): void {
    if (!this.quoteId) return;
    const key = `quote_public_link_${this.quoteId}`;
    if (this.publicLinkUrl) {
      sessionStorage.setItem(key, this.publicLinkUrl);
    } else {
      sessionStorage.removeItem(key);
    }
  }

  // A partir del token revelado (solo en publish/rotate), arma la URL de la página pública del
  // front y la copia al portapapeles.
  private captureAndCopyLink(quote: QuoteResponse, successMsg: string): void {
    this.publicLinkUrl = quote.publicToken ? buildPublicQuoteUrl(quote.publicToken) : null;
    this.persistPublicLink();
    if (this.publicLinkUrl) {
      this.copyToClipboard(this.publicLinkUrl, successMsg);
    } else {
      this.toast.success({ title: 'Listo', message: 'Operación realizada.' });
    }
  }

  private copyToClipboard(text: string, successMsg: string): void {
    navigator.clipboard?.writeText(text).then(
      () => this.toast.success({ title: 'Enlace copiado', message: successMsg }),
      () => this.toast.info({ title: 'Copia manual', message: text })
    );
  }

  recalcular(): void {
    if (!this.quoteId) return;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.quoteApi.calculateTotals(this.quoteId, businessId).subscribe({
      next: (quote) => {
        this.currentQuote = quote;
        this.successMessage = 'Totales recalculados correctamente';
        this.cargarQuote(this.quoteId!);
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al recalcular totales';
      }
    });
  }

  descargarPdf(): void {
    if (!this.quoteId) return;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.quoteApi.downloadPdf(this.quoteId, businessId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `quote-${this.quoteId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al descargar el PDF';
      }
    });
  }

  cancelar(): void {
    this.cerrarModal();
  }

  // ---- Versionado (#6) ----

  // Abre el modal de historial y carga la familia de versiones (mismo quoteNumber).
  verVersiones(): void {
    if (!this.quoteId) return;
    this.showVersionsModal = true;
    this.cargarVersiones();
  }

  cerrarVersiones(): void {
    this.showVersionsModal = false;
  }

  private cargarVersiones(): void {
    if (!this.quoteId) return;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.loadingVersions = true;
    this.quoteApi.getVersions(this.quoteId, businessId).subscribe({
      next: (versions) => {
        this.versions = versions;
        this.loadingVersions = false;
      },
      error: (err) => {
        this.loadingVersions = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo cargar el historial de versiones' });
      }
    });
  }

  // Crea una nueva versión (mismo número, v+1, Borrador) y navega a ella.
  async nuevaVersion(): Promise<void> {
    if (!this.quoteId || !this.currentQuote) return;

    const versionNotes = await this.confirmDialog.prompt({
      title: 'Nueva versión',
      message: `Nueva versión de "${this.currentQuote.quoteNumber}". La versión vigente pasará a "Reemplazada". Ingrese notas de versión (opcional):`,
      inputType: 'textarea',
      required: false
    });
    if (versionNotes === null) return; // cancelado

    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.creatingVersion = true;
    this.quoteApi.createNewVersion(this.quoteId, businessId, versionNotes || undefined).subscribe({
      next: (newQuote) => {
        this.creatingVersion = false;
        this.toast.created('Versión de cotización');
        this.irACotizacion(newQuote.id);
      },
      error: (err) => {
        this.creatingVersion = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al crear nueva versión' });
      }
    });
  }

  // Restaura una versión histórica: el backend crea una nueva versión vigente (Borrador)
  // clonando la histórica. Navegamos a la versión restaurada devuelta.
  async restaurarVersion(version: QuoteVersionResponse): Promise<void> {
    if (version.isCurrent) return;

    const ok = await this.confirmDialog.confirm({
      title: 'Restaurar versión',
      message: `Se creará una nueva versión vigente (Borrador) a partir de la v${version.version}. La versión vigente actual pasará a "Reemplazada". ¿Continuar?`,
      confirmText: 'Restaurar'
    });
    if (!ok) return;

    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.restoringVersionId = version.id;
    this.quoteApi.restoreVersion(version.id, businessId).subscribe({
      next: (restored) => {
        this.restoringVersionId = null;
        this.showVersionsModal = false;
        this.toast.success({ title: 'Versión restaurada', message: `Se restauró la v${version.version} como nueva versión vigente.` });
        this.irACotizacion(restored.id);
      },
      error: (err) => {
        this.restoringVersionId = null;
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo restaurar la versión' });
      }
    });
  }

  // ---- Duplicar (#12) ----

  // Duplica como una cotización NUEVA e independiente (número nuevo, Borrador, v1) y navega a la copia.
  duplicar(): void {
    if (!this.quoteId) return;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.duplicating = true;
    this.quoteApi.duplicateQuote(this.quoteId, businessId).subscribe({
      next: (copy) => {
        this.duplicating = false;
        this.toast.created('Cotización');
        this.irACotizacion(copy.id);
      },
      error: (err) => {
        this.duplicating = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo duplicar la cotización' });
      }
    });
  }

  // ---- Aprobación interna (RF-001) ----

  // Carga la solicitud PENDING vigente solo cuando la cotización está en espera
  // (para el hint "En espera de aprobación de {aprobador}"). Error → silencio: el hint es accesorio.
  private cargarAprobacionPendiente(quote: QuoteResponse): void {
    if (quote.statusName !== QUOTE_STATUS_PENDING_APPROVAL) {
      this.pendingApproval = null;
      return;
    }
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.quoteApprovalApi.getApprovals(quote.id, businessId).subscribe({
      next: (approvals) => {
        this.pendingApproval = approvals.find(a => a.status === 'PENDING') || null;
      },
      error: () => {
        this.pendingApproval = null;
      }
    });
  }

  solicitarAprobacion(): void {
    if (!this.quoteId) return;
    this.showRequestApprovalModal = true;
  }

  cerrarSolicitarAprobacion(): void {
    this.showRequestApprovalModal = false;
  }

  // La solicitud ya fue creada (el modal hizo el POST y el toast): refrescar la cotización,
  // que ahora está en "Pendiente de Aprobación".
  onAprobacionSolicitada(): void {
    this.showRequestApprovalModal = false;
    if (this.quoteId) {
      this.cargarQuote(this.quoteId);
    }
  }

  get isPendingApproval(): boolean {
    return this.currentQuote?.statusName === QUOTE_STATUS_PENDING_APPROVAL;
  }

  get isAprobada(): boolean {
    return this.currentQuote?.statusName === QUOTE_STATUS_APPROVED_INTERNAL;
  }

  get pendingApproverName(): string {
    return this.pendingApproval?.approverName || 'el aprobador asignado';
  }

  /**
   * Bloqueo por flag "requiere aprobación interna": en Borrador con el flag activo,
   * Publicar y Enviar al cliente quedan deshabilitados (el backend devuelve 422) y
   * la acción esperada es "Solicitar aprobación". Tras "Aprobada" se habilitan normal.
   * Se lee el valor del form (igual al guardado tras cargar) para reflejar la intención actual.
   */
  get isApprovalBlocked(): boolean {
    return this.currentQuote?.statusName === 'Borrador' && !!this.form.get('requiresApproval')?.value;
  }

  readonly APPROVAL_BLOCKED_HINT = 'Requiere aprobación interna — solicita aprobación primero';

  // ---- Envío por correo (#28) ----

  // Abre el modal de envío precargando como chip SOLO el contacto principal del negocio (deal) con correo;
  // los demás contactos con correo quedan como sugerencias clicables. Si la cotización no tiene deal o la
  // llamada falla, cae al contacto principal de la cotización.
  abrirEnviarCorreo(): void {
    // En "Pendiente de Aprobación" o en Borrador con "requiere aprobación interna" el envío
    // está bloqueado (el backend devuelve 422): cubre también la navegación con ?enviar=1
    // desde la lista/detalle.
    if (!this.quoteId || this.isPendingApproval || this.isApprovalBlocked) return;
    this.sendEmailError = '';
    this.sendRecipients = [];
    this.sendRecipientInput = '';
    this.sendRecipientInputError = '';
    this.sendSuggestions = [];
    this.showAllSuggestions = false;
    this.dealContactsSinCorreo = [];
    this.sendCc = '';
    this.sendPersonalMessage = '';
    this.showSendEmailModal = true;

    const dealId = this.currentQuote?.dealId;
    if (!dealId) {
      this.precargarContactoPrincipal();
      return;
    }

    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.loadingDealContacts = true;
    this.dealContactApi.getByDeal(dealId, businessId).subscribe({
      next: (contacts) => {
        this.loadingDealContacts = false;
        this.precargarDestinatariosDesdeDeal(contacts);
      },
      error: () => {
        // El modal sigue funcionando igual, solo sin chips precargados del deal.
        this.loadingDealContacts = false;
        this.precargarContactoPrincipal();
      }
    });
  }

  // Reparte los contactos del deal: SOLO el principal con correo entra como chip; el resto con correo
  // queda como sugerencias clicables. Los que no tienen correo se listan como informativos.
  private precargarDestinatariosDesdeDeal(contacts: DealContactResponse[]): void {
    const ordered = [...contacts].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
    let principalAgregado = false;
    for (const c of ordered) {
      const email = (c.contactMail || '').trim();
      const name = [c.contactFullname, c.contactLastname].filter(Boolean).join(' ').trim();
      if (!email || !this.isValidEmail(email)) {
        this.dealContactsSinCorreo.push(name || `Contacto #${c.idContact}`);
        continue;
      }
      const chip: SendRecipientChip = {
        email,
        name: name || undefined,
        isPrimary: c.isPrimary,
        roleName: c.roleName || undefined,
        fromContact: true
      };
      if (c.isPrimary && !principalAgregado) {
        principalAgregado = this.agregarChip(chip);
      } else {
        this.agregarSugerencia(chip);
      }
    }
    // Red de seguridad: sin contacto principal con correo, usar el principal de la cotización.
    if (!principalAgregado) {
      this.precargarContactoPrincipal();
    }
  }

  // Fallback: chip único con el contacto principal que trae el QuoteResponse (si tiene correo).
  // agregarChip consume la sugerencia equivalente si ese correo ya estaba sugerido.
  private precargarContactoPrincipal(): void {
    const email = (this.currentQuote?.contactEmail || '').trim();
    if (!email || !this.isValidEmail(email)) return;
    this.agregarChip({
      email,
      name: this.currentQuote?.contactFullName?.trim() || undefined,
      isPrimary: true,
      fromContact: true
    });
  }

  // Agrega un chip evitando duplicados por correo (case-insensitive). Si el correo coincide con una
  // sugerencia, la consume (hereda nombre/rol y vuelve a sugerencias al quitarse). Devuelve true si lo agregó.
  private agregarChip(chip: SendRecipientChip): boolean {
    const key = chip.email.toLowerCase();
    if (this.sendRecipients.some(r => r.email.toLowerCase() === key)) return false;
    const idx = this.sendSuggestions.findIndex(s => s.email.toLowerCase() === key);
    if (idx >= 0) {
      const sugerencia = this.sendSuggestions.splice(idx, 1)[0];
      chip = {
        ...sugerencia,
        ...chip,
        name: chip.name || sugerencia.name,
        roleName: chip.roleName || sugerencia.roleName,
        fromContact: true
      };
    }
    this.sendRecipients.push(chip);
    return true;
  }

  // Guarda una sugerencia evitando duplicados contra chips ya agregados y otras sugerencias.
  private agregarSugerencia(chip: SendRecipientChip): void {
    const key = chip.email.toLowerCase();
    if (this.sendRecipients.some(r => r.email.toLowerCase() === key)) return;
    if (this.sendSuggestions.some(s => s.email.toLowerCase() === key)) return;
    this.sendSuggestions.push(chip);
  }

  // Clic en una píldora de sugerencia: pasa a ser chip de destinatario (agregarChip la consume de la lista).
  seleccionarSugerencia(sugerencia: SendRecipientChip): void {
    if (this.sendingEmail) return;
    this.agregarChip(sugerencia);
  }

  // Sugerencias visibles: colapsadas a SUGGESTIONS_VISIBLE salvo que el usuario expanda.
  get visibleSuggestions(): SendRecipientChip[] {
    return this.showAllSuggestions
      ? this.sendSuggestions
      : this.sendSuggestions.slice(0, this.SUGGESTIONS_VISIBLE);
  }

  get hiddenSuggestionsCount(): number {
    return Math.max(0, this.sendSuggestions.length - this.SUGGESTIONS_VISIBLE);
  }

  toggleVerMasSugerencias(): void {
    this.showAllSuggestions = !this.showAllSuggestions;
  }

  // Agrega destinatario(s) desde el input de texto. Acepta varios correos separados por coma.
  agregarDestinatario(): void {
    this.sendRecipientInputError = '';
    const raw = this.sendRecipientInput.trim();
    if (!raw) return;

    const emails = raw.split(',').map(e => e.trim()).filter(e => e.length > 0);
    for (const email of emails) {
      if (!this.isValidEmail(email)) {
        this.sendRecipientInputError = `El correo "${email}" no es válido`;
        return;
      }
    }
    emails.forEach(email => this.agregarChip({ email }));
    this.sendRecipientInput = '';
  }

  quitarDestinatario(index: number): void {
    if (this.sendingEmail) return;
    const removed = this.sendRecipients.splice(index, 1)[0];
    // Los chips que provienen de contactos del negocio vuelven a las sugerencias (al frente, para que
    // se vean aun colapsadas); los de correo libre simplemente desaparecen.
    if (removed?.fromContact &&
        !this.sendSuggestions.some(s => s.email.toLowerCase() === removed.email.toLowerCase())) {
      this.sendSuggestions.unshift(removed);
    }
  }

  // Tooltip del chip: correo + rol en el deal si lo hay.
  chipTitle(chip: SendRecipientChip): string {
    const parts = [chip.email];
    if (chip.roleName) parts.push(chip.roleName);
    return parts.join(' — ');
  }

  cerrarEnviarCorreo(): void {
    if (this.sendingEmail) return;
    this.showSendEmailModal = false;
  }

  enviarCorreo(): void {
    if (!this.quoteId || this.sendingEmail) return;

    this.sendEmailError = '';

    // Si el usuario escribió un correo en el input y no presionó Enter, intentar agregarlo antes de enviar.
    if (this.sendRecipientInput.trim()) {
      this.agregarDestinatario();
      if (this.sendRecipientInputError) return;
    }

    // Validación ligera en cliente (el backend valida de nuevo con 400)
    if (this.sendRecipients.length === 0) {
      this.sendEmailError = 'Agrega al menos un destinatario para enviar la cotización';
      return;
    }
    const ccRecipients = this.parseCcEmails();
    if (ccRecipients === null) return; // error ya seteado
    if (this.sendPersonalMessage.length > this.SEND_EMAIL_MESSAGE_MAX) {
      this.sendEmailError = `El mensaje personal no puede superar los ${this.SEND_EMAIL_MESSAGE_MAX} caracteres`;
      return;
    }

    // Destinatarios desde los chips (la UI exige al menos 1; el default del backend queda como red de seguridad de API).
    const request: SendQuoteEmailRequest = {
      recipients: this.sendRecipients.map(chip => {
        const to: EmailRecipient = { email: chip.email };
        if (chip.name) to.name = chip.name;
        return to;
      })
    };
    if (ccRecipients.length > 0) {
      request.cc = ccRecipients;
    }
    const personalMessage = this.sendPersonalMessage.trim();
    if (personalMessage) {
      request.personalMessage = personalMessage;
    }

    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.sendingEmail = true;

    this.quoteApi.sendEmail(this.quoteId, businessId, request).subscribe({
      next: (response) => {
        this.sendingEmail = false;
        this.showSendEmailModal = false;
        // El enlace público se rota/emite en CADA envío y se revela solo en esta respuesta:
        // capturarlo igual que tras publicar/rotar para poder copiarlo desde el form.
        this.publicLinkUrl = response.quote.publicToken
          ? buildPublicQuoteUrl(response.quote.publicToken)
          : this.publicLinkUrl;
        this.persistPublicLink();
        this.toast.success({
          title: 'Cotización enviada',
          message: 'El correo se encoló para envío al cliente. Se generó un enlace nuevo; el anterior dejó de funcionar.'
        });
        this.cargarQuote(this.quoteId!);
      },
      error: (err) => {
        this.sendingEmail = false;
        const status = err?.status;
        const msg: string = err?.error?.message || 'No se pudo enviar la cotización por correo';
        if (status === 422 || status === 400) {
          // Sin destinatario resolvible / validación: dejar al usuario corregir en el modal.
          this.sendEmailError = msg;
        } else if (status === 409) {
          // Sin canal de correo conectado: aviso en el modal + toast con la indicación del backend.
          this.sendEmailError = msg;
          this.toast.error({ title: 'Canal de correo no conectado', message: msg });
        } else {
          this.sendEmailError = msg;
          this.toast.error({ title: 'Error', message: msg });
        }
      }
    });
  }

  // Parsea el campo CC (emails separados por coma). Devuelve null si hay un email inválido
  // (y deja el error seteado en sendEmailError).
  private parseCcEmails(): EmailRecipient[] | null {
    const raw = this.sendCc.trim();
    if (!raw) return [];
    const emails = raw.split(',').map(e => e.trim()).filter(e => e.length > 0);
    for (const email of emails) {
      if (!this.isValidEmail(email)) {
        this.sendEmailError = `El correo en CC "${email}" no es válido`;
        return null;
      }
    }
    return emails.map(email => ({ email }));
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // Navega a otra cotización en este mismo componente. Como la ruta se lee con snapshot,
  // recargamos el estado manualmente (Angular reutiliza la instancia al cambiar solo el :id).
  private irACotizacion(id: number): void {
    // Modo modal: recargar el wizard sobre la misma instancia, sin navegar.
    if (this.modalMode) {
      this.quoteId = id;
      this.isEditing = true;
      this.publicLinkUrl = null;
      this.versions = [];
      this.currentStep = 1;
      this.reviewDoc = null;
      this.revokePdf();
      this.cargarQuote(id);
      return;
    }
    this.router.navigate(['/cotizaciones/editar', id]).then(() => {
      this.quoteId = id;
      this.isEditing = true;
      this.publicLinkUrl = null;
      this.versions = [];
      this.currentStep = 1;
      this.reviewDoc = null;
      this.revokePdf();
      this.cargarQuote(id);
    });
  }

  get isPublicada(): boolean {
    return this.currentQuote?.statusName === 'Publicada';
  }

  formatDate(date?: string): string {
    return this.datetimeService.formatDateNumeric(date);
  }

  get isDraft(): boolean {
    return !this.currentQuote || this.currentQuote.statusName === 'Borrador';
  }

  // Rotar/Revocar el enlace público requieren rol Manager o superior (el backend lo enforce con 403).
  get canManageLink(): boolean {
    const role = this.authService.getCurrentUser()?.roleName;
    return role === 'Manager' || role === 'Admin' || role === 'Super Admin';
  }

  hasError(field: string): boolean {
    const control = this.form.get(field);
    return control ? control.invalid && control.touched : false;
  }

  hasItemError(index: number, field: string): boolean {
    const control = this.items.at(index).get(field);
    return control ? control.invalid && control.touched : false;
  }

  // ---- Cláusulas legales (V72) ----

  isClauseSelected(id: number): boolean {
    return this.selectedClauseIds.has(id);
  }

  toggleClause(id: number): void {
    if (this.selectedClauseIds.has(id)) {
      this.selectedClauseIds.delete(id);
    } else {
      this.selectedClauseIds.add(id);
    }
  }

  toggleClausesSection(): void {
    this.showClauses = !this.showClauses;
  }

  getError(field: string): string {
    const control = this.form.get(field);
    if (!control || !control.errors) return '';

    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['discountPercentMax']) return 'El descuento por porcentaje no puede superar 100%';
    if (control.errors['issuedTooOld']) return 'La fecha de emisión no puede ser anterior a 3 días';
    if (control.errors['expiresPast']) return 'La fecha de validez debe ser igual o posterior a hoy';
    if (control.errors['min']) return `El valor debe ser positivo (mínimo ${control.errors['min'].min})`;
    if (control.errors['max']) return `Valor máximo: ${control.errors['max'].max}`;
    if (control.errors['maxlength']) return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;

    return 'Campo inválido';
  }

  onItemTypeChange(index: number): void {
    const itemFormGroup = this.items.at(index) as FormGroup;
    itemFormGroup.patchValue({
      productId: null,
      bundleId: null,
      itemName: '',
      unitPrice: 0,
      // (INC33) La variante solo existe en ítems 'product' y atada al producto: al cambiar de tipo
      // deja de tener sentido y el backend rechazaría el request (400).
      variantId: null,
      variantName: null,
      variantSku: null,
      discountRuleId: null
    });
    this.itemProductSearchTerms[index] = '';
    this.itemBundleSearchTerms[index] = '';
    this.tierAppliedItems[index] = false;
    delete this.itemVariants[index];
    this.variantPriceApplied[index] = false;
    // Se limpió el producto: quitar cualquier descuento de regla auto-aplicado y el control explícito.
    delete this.itemDiscountRules[index];
    this.resetControlDescuentoLinea(index);
  }

  /** (INC32) Devuelve la línea al descuento automático y descarta reglas/cupón del producto anterior. */
  private resetControlDescuentoLinea(index: number): void {
    delete this.itemDiscountChoice[index];
    delete this.itemApplicableRules[index];
    delete this.itemCouponResult[index];
    this.itemCouponCode[index] = '';
    this.itemCouponError[index] = '';
  }

  onProductSelect(index: number, productId: number | null): void {
    if (!productId) return;

    const product = this.products.find(p => p.id === productId);
    if (product) {
      const itemFormGroup = this.items.at(index) as FormGroup;
      itemFormGroup.patchValue({
        itemName: product.name,
        description: product.description || '',
        unitPrice: product.price,
        itemCode: product.sku || '',
        // Prefijar la categoría desde el producto si la tenemos (el backend igual la autocompleta al guardar).
        category: product.categoryName || '',
        // Cambió el producto: la variante del anterior ya no aplica.
        variantId: null,
        variantName: null,
        variantSku: null
      });
      this.variantPriceApplied[index] = false;
      this.aplicarPrecioDeLista(index, productId);
      this.aplicarTierPricing(index);
      // (INC33/INC32) Variantes y reglas del producto elegido, para los selectores de la línea.
      this.cargarVariantesLinea(index);
      this.resetControlDescuentoLinea(index);
      this.cargarReglasAplicables(index);
    }
  }

  // ------------------------------------------------------------
  // Lista de precios ligada a la cotización (V75)
  // ------------------------------------------------------------

  /** Id de la lista de precios seleccionada en el form (null si no hay). */
  private get selectedPriceListId(): number | null {
    const v = this.form.get('priceListId')?.value;
    return v != null ? Number(v) : null;
  }

  /**
   * Si hay una lista de precios seleccionada, consulta el precio resuelto del producto en esa
   * lista y lo aplica como precio unitario de la fila. Si el producto no está en la lista (404)
   * o falla, NO toca el precio (queda el precio base ya seteado). El usuario puede editarlo.
   */
  private aplicarPrecioDeLista(index: number, productId: number): void {
    const priceListId = this.selectedPriceListId;
    if (!priceListId) return;
    this.priceListApi.getProductPrice(priceListId, productId).subscribe({
      next: (item) => {
        const group = this.items.at(index) as FormGroup | null;
        if (!group) return;
        // Solo aplica si la fila sigue siendo el mismo producto.
        if (Number(group.get('productId')?.value) !== productId) return;
        if (item?.resolvedPrice != null) {
          group.get('unitPrice')?.setValue(item.resolvedPrice);
          this.tierAppliedItems[index] = false;
        }
        // El precio cambió: re-resolver el descuento vigente sobre el nuevo precio.
        this.resolverDescuentoLinea(index);
      },
      error: () => { /* 404 / error: conservar el precio base del producto */ }
    });
  }

  /** Variante para el panel "Agregar ítem": aplica el precio de lista sobre el estado del panel. */
  private aplicarPrecioDeListaAddForm(productId: number): void {
    const priceListId = this.selectedPriceListId;
    if (!priceListId) return;
    this.priceListApi.getProductPrice(priceListId, productId).subscribe({
      next: (item) => {
        if (this.addFormProductId !== productId) return;
        if (item?.resolvedPrice != null) {
          this.addFormUnitPrice = item.resolvedPrice;
          this.addFormTierApplied = false;
        }
        // El precio cambió: re-resolver el descuento vigente del panel.
        this.resolverDescuentoAddForm();
      },
      error: () => { /* 404 / error: conservar el precio base */ }
    });
  }

  // ------------------------------------------------------------
  // Reglas de descuento vigentes (Inc 40)
  // ------------------------------------------------------------

  /**
   * Resuelve el mejor descuento vigente para una línea (producto + cantidad + precio unitario) y lo
   * APLICA automáticamente sobre los campos de descuento de la fila, de modo que el cálculo lo refleje
   * sin que el usuario ajuste nada. Si no aplica ninguna regla y previamente se había auto-aplicado
   * una, se limpia. Solo corre para ítems de tipo 'product' con productId. Falla en silencio.
   */
  private resolverDescuentoLinea(index: number): void {
    const group = this.items.at(index) as FormGroup | null;
    if (!group) return;
    // (INC32) El auto-aplicado solo manda mientras la línea esté en modo 'auto'. Si el usuario
    // eligió una regla concreta, aplicó un cupón o desactivó el descuento, su decisión gana:
    // se recalcula esa elección sobre la nueva cantidad/precio, sin volver al automático.
    const choice = this.discountChoiceOf(index);
    if (choice !== 'auto') {
      this.reaplicarEleccionDescuento(index);
      return;
    }
    const v = group.value;
    if (v.itemType !== 'product' || !v.productId) {
      this.limpiarDescuentoLinea(index, group);
      return;
    }
    const productId = Number(v.productId);
    const qty = Number(v.quantity);
    const unit = Number(v.unitPrice);
    if (!qty || qty <= 0 || unit == null || unit < 0 || isNaN(unit)) return;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.discountRuleApi.resolveDiscount(productId, qty, unit, businessId, this.quoteContactId).subscribe({
      next: (res) => {
        // La fila pudo cambiar de producto/cantidad/precio mientras respondía: descartar si ya no aplica.
        if (Number(group.get('productId')?.value) !== productId) return;
        if (Number(group.get('quantity')?.value) !== qty) return;
        if (Number(group.get('unitPrice')?.value) !== unit) return;
        if (res.applied) {
          this.itemDiscountRules[index] = res;
          this.aplicarDescuentoResuelto(group, res);
        } else {
          this.limpiarDescuentoLinea(index, group);
        }
      },
      error: () => { /* sin descuento: conservar lo que haya en la línea */ }
    });
  }

  /**
   * Vuelca el descuento resuelto a la línea. SIEMPRE en `discountAmount` (nunca en `discountPercent`),
   * incluso para reglas de tipo 'percent': el `calculateTotals` del backend ignora `discountPercent`
   * — lo persiste pero no lo usa —, así que un descuento escrito solo como % se perdería al guardar
   * y el total del documento no coincidiría con el que muestra el editor. El % configurado por la
   * regla sigue siendo visible en el aviso y en el selector de regla de la línea.
   */
  private aplicarDescuentoResuelto(group: FormGroup, res: ResolveDiscountResponse): void {
    group.patchValue(
      {
        discountType: 'fixed',
        discountAmount: res.discountAmount ?? 0,
        discountPercent: 0,
        discountRuleId: res.ruleId ?? null
      },
      { emitEvent: false }
    );
  }

  /** Quita el descuento auto-aplicado de una línea (solo si lo puso una regla, no el usuario). */
  private limpiarDescuentoLinea(index: number, group: FormGroup): void {
    if (this.itemDiscountRules[index]) {
      delete this.itemDiscountRules[index];
      group.patchValue({ discountPercent: 0, discountAmount: 0, discountRuleId: null }, { emitEvent: false });
    }
  }

  /** ¿La línea tiene un descuento de regla vigente aplicado? (para el aviso en el template). */
  hasItemDiscountRule(index: number): boolean {
    return !!this.itemDiscountRules[index]?.applied;
  }

  /** Descuento resuelto de la línea (o null). */
  itemDiscountRule(index: number): ResolveDiscountResponse | null {
    return this.itemDiscountRules[index] ?? null;
  }

  /**
   * Variante para el panel "Agregar ítem": resuelve el descuento sobre el estado del panel y lo
   * vuelca a addFormDiscountPercent / addFormDiscountAmount. Solo para productos con producto elegido.
   */
  private resolverDescuentoAddForm(): void {
    if (this.addFormItemType !== 'product' || !this.addFormProductId) {
      this.addFormDiscountRule = null;
      return;
    }
    const productId = this.addFormProductId;
    const qty = Number(this.addFormQuantity);
    const unit = Number(this.addFormUnitPrice);
    if (!qty || qty <= 0 || unit == null || unit < 0 || isNaN(unit)) return;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.discountRuleApi.resolveDiscount(productId, qty, unit, businessId, this.quoteContactId).subscribe({
      next: (res) => {
        if (this.addFormProductId !== productId) return;
        if (Number(this.addFormQuantity) !== qty || Number(this.addFormUnitPrice) !== unit) return;
        if (res.applied) {
          this.addFormDiscountRule = res;
          // El descuento va siempre al monto fijo: el backend no calcula con discountPercent.
          this.addFormDiscountAmount = res.discountAmount ?? 0;
          this.addFormDiscountPercent = 0;
        } else if (this.addFormDiscountRule) {
          // Se había auto-aplicado antes y ya no aplica: limpiar.
          this.addFormDiscountRule = null;
          this.addFormDiscountPercent = 0;
          this.addFormDiscountAmount = 0;
        }
      },
      error: () => { /* sin descuento: conservar el estado del panel */ }
    });
  }

  // ------------------------------------------------------------
  // (INC32) Reglas de descuento visibles y gobernables por línea
  // ------------------------------------------------------------

  /**
   * Contacto/cliente de la cotización, para que el `resolve` considere también las reglas
   * CONTRACTUALES de ESE cliente además de las generales. undefined = solo reglas generales.
   */
  private get quoteContactId(): number | undefined {
    const v = this.form.get('idContactPrimary')?.value;
    return v != null ? Number(v) : undefined;
  }

  /** Modo de descuento de la línea. Por defecto 'auto' = comportamiento auto-aplicado (Inc 40). */
  discountChoiceOf(index: number): string {
    return this.itemDiscountChoice[index] || 'auto';
  }

  /** Reglas que el usuario puede elegir explícitamente para la línea. */
  applicableRulesFor(index: number): DiscountRuleResponse[] {
    return this.itemApplicableRules[index] || [];
  }

  /**
   * Carga las reglas de descuento del producto de la línea para poblar el selector explícito.
   * Se filtran los tipos que no son elegibles a mano:
   *   - 'coupon'    → se aplica con su código (campo de cupón), no desde el selector.
   *   - 'min_price' → es un piso de precio, no un descuento.
   * También se descartan las vencidas / aún no vigentes. Las que quedan fuera del rango de
   * cantidad SÍ se listan (marcadas): elegirlas es justamente "forzar" el descuento.
   * Falla en silencio: sin reglas, el selector no se muestra.
   */
  private cargarReglasAplicables(index: number): void {
    const group = this.items.at(index) as FormGroup | null;
    if (!group) return;
    const v = group.value;
    if (v.itemType !== 'product' || !v.productId) {
      delete this.itemApplicableRules[index];
      return;
    }
    const productId = Number(v.productId);
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.discountRuleApi.getApplicableDiscountRules(productId, businessId).subscribe({
      next: (rules) => {
        // La fila pudo cambiar de producto mientras respondía: descartar si ya no aplica.
        if (Number(group.get('productId')?.value) !== productId) return;
        const elegibles = (rules || []).filter(r => this.isSelectableRule(r));
        if (elegibles.length > 0) this.itemApplicableRules[index] = elegibles;
        else delete this.itemApplicableRules[index];
      },
      error: () => { delete this.itemApplicableRules[index]; }
    });
  }

  /** ¿La regla se puede elegir a mano? Solo 'volume'/'contractual' vigentes (mismo criterio del backend). */
  private isSelectableRule(rule: DiscountRuleResponse): boolean {
    if (rule.ruleType !== 'volume' && rule.ruleType !== 'contractual') return false;
    if (rule.isActive === false || rule.isExpired) return false;
    const hoy = this.today;
    if (rule.validFrom && rule.validFrom.substring(0, 10) > hoy) return false;
    if (rule.validUntil && rule.validUntil.substring(0, 10) < hoy) return false;
    return true;
  }

  /** ¿La regla queda fuera del rango de cantidad de la línea? (se marca en el selector). */
  isRuleOutOfQuantityRange(index: number, rule: DiscountRuleResponse): boolean {
    const qty = Number(this.items.at(index)?.get('quantity')?.value);
    if (!qty || isNaN(qty)) return false;
    if (rule.minQuantity != null && qty < rule.minQuantity) return true;
    if (rule.maxQuantity != null && qty > rule.maxQuantity) return true;
    return false;
  }

  /** Etiqueta legible de una regla para el selector ("Por volumen · 10% · desde 5 und"). */
  ruleLabel(rule: DiscountRuleResponse): string {
    const tipo = rule.ruleType === 'volume' ? 'Por volumen' : 'Contractual';
    const valor = rule.discountType === 'percent'
      ? `${rule.discountValue ?? 0}%`
      : `${this.currencySymbol} ${rule.discountValue ?? 0}`;
    const partes = [tipo, valor];
    if (rule.minQuantity != null) partes.push(`desde ${rule.minQuantity}`);
    if (rule.contactName) partes.push(rule.contactName);
    return partes.join(' · ');
  }

  /**
   * Mismo cálculo que `computeDiscountAmount` del backend, para que lo que ve el usuario al forzar
   * una regla coincida con lo que se persiste: percent → total × valor / 100 (2 decimales);
   * fixed_amount → monto fijo acotado al total de la línea.
   */
  private computeRuleDiscountAmount(rule: DiscountRuleResponse, lineTotal: number): number {
    const valor = rule.discountValue ?? 0;
    if (rule.discountType === 'percent') return this.round2(lineTotal * (valor / 100));
    return Math.min(valor, lineTotal);
  }

  /** Total de la línea antes de descuentos (cantidad × precio unitario). */
  private lineTotalOf(group: FormGroup): number {
    const qty = Number(group.get('quantity')?.value) || 0;
    const unit = Number(group.get('unitPrice')?.value) || 0;
    return qty * unit;
  }

  /** El usuario cambió el modo de descuento de la línea desde el selector. */
  onItemDiscountChoiceChange(index: number, choice: string): void {
    this.itemDiscountChoice[index] = choice;
    // Salir del modo cupón descarta el cupón resuelto (no se vuelve a consumir al reelegirlo).
    if (choice !== 'coupon') {
      delete this.itemCouponResult[index];
      this.itemCouponError[index] = '';
    }
    if (choice === 'auto') {
      // Volver al automático: limpiar lo forzado y dejar que la regla vigente decida de nuevo.
      const group = this.items.at(index) as FormGroup | null;
      if (group) {
        delete this.itemDiscountRules[index];
        group.patchValue({ discountPercent: 0, discountAmount: 0, discountRuleId: null }, { emitEvent: false });
      }
      this.resolverDescuentoLinea(index);
      return;
    }
    this.reaplicarEleccionDescuento(index);
  }

  /**
   * Recalcula el descuento de la línea según la elección explícita del usuario (regla forzada,
   * cupón o sin descuento). Se invoca también cuando cambia la cantidad o el precio, para que la
   * elección siga siendo coherente sin volver al automático.
   */
  private reaplicarEleccionDescuento(index: number): void {
    const group = this.items.at(index) as FormGroup | null;
    if (!group) return;
    const choice = this.discountChoiceOf(index);

    if (choice === 'none') {
      delete this.itemDiscountRules[index];
      group.patchValue({ discountPercent: 0, discountAmount: 0, discountRuleId: null }, { emitEvent: false });
      return;
    }

    if (choice === 'coupon') {
      const coupon = this.itemCouponResult[index];
      if (coupon) this.aplicarCuponResuelto(index, group, coupon);
      return;
    }

    if (choice.startsWith('rule:')) {
      const ruleId = Number(choice.substring(5));
      const rule = this.applicableRulesFor(index).find(r => r.id === ruleId);
      if (rule) this.aplicarReglaExplicita(index, group, rule);
    }
  }

  /** Aplica una regla elegida a mano y arma el aviso con el mismo shape del auto-aplicado. */
  private aplicarReglaExplicita(index: number, group: FormGroup, rule: DiscountRuleResponse): void {
    const lineTotal = this.lineTotalOf(group);
    const qty = Number(group.get('quantity')?.value) || 0;
    const amount = this.computeRuleDiscountAmount(rule, lineTotal);
    const finalTotal = Math.max(0, lineTotal - amount);
    const valor = rule.discountType === 'percent'
      ? `${rule.discountValue ?? 0}%`
      : `${this.currencySymbol} ${rule.discountValue ?? 0}`;
    this.itemDiscountRules[index] = {
      applied: true,
      ruleId: rule.id,
      ruleType: rule.ruleType,
      discountType: rule.discountType,
      discountValue: rule.discountValue,
      discountAmount: amount,
      lineTotal,
      finalTotal,
      finalUnitPrice: qty > 0 ? this.round2(finalTotal / qty) : null,
      message: `Regla aplicada a mano: ${valor}`
    };
    // El monto SIEMPRE en discountAmount: el backend no calcula con discountPercent.
    group.patchValue(
      { discountType: 'fixed', discountAmount: amount, discountPercent: 0, discountRuleId: rule.id },
      { emitEvent: false }
    );
  }

  // ---- Cupones (POST /api/product-discount-rules/apply-coupon) ----

  /** Pide el cupón tecleado y, si es válido, lo aplica como descuento de la línea. */
  aplicarCuponLinea(index: number): void {
    const group = this.items.at(index) as FormGroup | null;
    if (!group) return;
    const code = (this.itemCouponCode[index] || '').trim();
    if (!code) {
      this.itemCouponError[index] = 'Ingresa un código de cupón';
      return;
    }
    const v = group.value;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.applyingCoupon[index] = true;
    this.itemCouponError[index] = '';

    this.discountRuleApi.applyCoupon({
      couponCode: code,
      businessId,
      productId: v.productId ?? undefined,
      quantity: Number(v.quantity) || 1
    }).subscribe({
      next: (res) => {
        this.applyingCoupon[index] = false;
        this.itemCouponResult[index] = res;
        this.itemDiscountChoice[index] = 'coupon';
        this.aplicarCuponResuelto(index, group, res);
        this.toast.success({ title: 'Cupón aplicado', message: res.message || `Cupón ${res.couponCode} aplicado a la línea` });
      },
      error: (err) => {
        this.applyingCoupon[index] = false;
        const msg = err.error?.message || 'El cupón no es válido para este ítem';
        this.itemCouponError[index] = msg;
        this.toast.error({ title: 'Cupón no aplicado', message: msg });
      }
    });
  }

  /**
   * Vuelca a la línea el descuento de un cupón ya resuelto. El recálculo por cambio de cantidad/precio
   * se hace aquí en cliente (mismo cálculo del backend) para NO reconsumir un uso del cupón.
   * `discountRuleId` queda en null: la respuesta del cupón no expone el id de la regla.
   */
  private aplicarCuponResuelto(index: number, group: FormGroup, coupon: ApplyCouponResponse): void {
    const lineTotal = this.lineTotalOf(group);
    const qty = Number(group.get('quantity')?.value) || 0;
    const amount = coupon.discountType === 'percent'
      ? this.round2(lineTotal * ((coupon.discountValue ?? 0) / 100))
      : Math.min(coupon.discountValue ?? coupon.resolvedDiscount ?? 0, lineTotal);
    const finalTotal = Math.max(0, lineTotal - amount);
    this.itemDiscountRules[index] = {
      applied: true,
      ruleId: null,
      ruleType: 'coupon',
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      discountAmount: amount,
      lineTotal,
      finalTotal,
      finalUnitPrice: qty > 0 ? this.round2(finalTotal / qty) : null,
      message: coupon.message || `Cupón ${coupon.couponCode} aplicado`
    };
    group.patchValue(
      { discountType: 'fixed', discountAmount: amount, discountPercent: 0, discountRuleId: null },
      { emitEvent: false }
    );
  }

  /** Quita el cupón de la línea y vuelve al descuento automático. */
  quitarCuponLinea(index: number): void {
    this.itemCouponCode[index] = '';
    this.itemCouponError[index] = '';
    delete this.itemCouponResult[index];
    this.onItemDiscountChoiceChange(index, 'auto');
  }

  /** ¿La línea tiene un cupón aplicado? (para el bloque de cupón del template). */
  hasItemCoupon(index: number): boolean {
    return !!this.itemCouponResult[index];
  }

  /** Limpia todo el estado indexado de ítems añadido por INC32/INC33. */
  private limpiarEstadoItemsExtendido(): void {
    this.itemDiscountChoice = {};
    this.itemApplicableRules = {};
    this.itemCouponCode = {};
    this.itemCouponError = {};
    this.itemCouponResult = {};
    this.applyingCoupon = {};
    this.itemVariants = {};
    this.variantPriceApplied = {};
  }

  // ------------------------------------------------------------
  // (INC33) Variantes de producto por línea
  // ------------------------------------------------------------

  /** Variantes activas del producto de la línea (vacío = no mostrar el selector). */
  variantsFor(index: number): ProductVariantResponse[] {
    return this.itemVariants[index] || [];
  }

  /** ¿El producto de la línea tiene variantes? Si no, no se renderiza nada (sin ruido visual). */
  hasVariants(index: number): boolean {
    return this.variantsFor(index).length > 0;
  }

  /**
   * Consulta las variantes del producto de la línea. Si no tiene, la clave se borra y el selector
   * no aparece. Solo aplica a ítems de tipo 'product' (el backend rechaza variantId en otros tipos).
   * Falla en silencio.
   */
  private cargarVariantesLinea(index: number): void {
    const group = this.items.at(index) as FormGroup | null;
    if (!group) return;
    const v = group.value;
    if (v.itemType !== 'product' || !v.productId) {
      delete this.itemVariants[index];
      return;
    }
    const productId = Number(v.productId);
    this.variantApi.getProductVariants(productId).subscribe({
      next: (variants) => {
        // La fila pudo cambiar de producto mientras respondía: descartar si ya no aplica.
        if (Number(group.get('productId')?.value) !== productId) return;
        const activas = (variants || []).filter(x => x.isActive);
        if (activas.length > 0) this.itemVariants[index] = activas;
        else delete this.itemVariants[index];
      },
      error: () => { delete this.itemVariants[index]; }
    });
  }

  /** Etiqueta de una variante en el selector ("Rojo / Talla L — SKU · S/ 120.00"). */
  variantLabel(variant: ProductVariantResponse): string {
    const nombre = variant.attributesSummary || variant.name || variant.sku;
    return `${nombre} — ${variant.sku}`;
  }

  /**
   * El usuario eligió (o quitó) una variante en la línea. Al elegirla se autocompletan el precio
   * efectivo y el SKU para que los totales en vivo sean correctos; el request viaja con unitPrice=0
   * mientras el usuario no toque el precio, de modo que el backend resuelva el valor canónico.
   * Al quitarla se restauran el precio y el SKU base del producto.
   */
  onItemVariantChange(index: number, variantId: number | null): void {
    const group = this.items.at(index) as FormGroup | null;
    if (!group) return;

    if (variantId == null) {
      const product = this.products.find(p => p.id === Number(group.get('productId')?.value));
      group.patchValue(
        {
          variantId: null,
          variantName: null,
          variantSku: null,
          ...(product ? { unitPrice: product.price, itemCode: product.sku || '' } : {})
        },
        { emitEvent: false }
      );
      this.variantPriceApplied[index] = false;
      // Vuelta al precio base: recalcular tramos y descuento vigente.
      this.aplicarTierPricing(index);
      this.resolverDescuentoLinea(index);
      return;
    }

    const variant = this.variantsFor(index).find(v => v.id === variantId);
    if (!variant) return;
    group.patchValue(
      {
        variantId: variant.id,
        variantName: variant.attributesSummary || variant.name || variant.sku,
        variantSku: variant.sku,
        unitPrice: variant.effectivePrice,
        itemCode: variant.sku
      },
      { emitEvent: false }
    );
    this.variantPriceApplied[index] = true;
    // El precio de la variante manda sobre los tramos del producto base.
    this.tierAppliedItems[index] = false;
    this.resolverDescuentoLinea(index);
  }

  /** Variante elegida en la línea (para el resumen de la fila). */
  itemVariantName(index: number): string | null {
    return (this.items.at(index)?.get('variantName')?.value as string | null) || null;
  }

  // ---- Variantes del panel "Agregar ítem" ----

  /** Carga las variantes del producto elegido en el panel de alta. */
  private cargarVariantesAddForm(): void {
    this.addFormVariants = [];
    this.addFormVariantId = null;
    this.addFormVariantName = null;
    this.addFormVariantSku = null;
    this.addFormVariantPriceApplied = false;
    if (this.addFormItemType !== 'product' || !this.addFormProductId) return;
    const productId = this.addFormProductId;
    this.variantApi.getProductVariants(productId).subscribe({
      next: (variants) => {
        if (this.addFormProductId !== productId) return;
        this.addFormVariants = (variants || []).filter(v => v.isActive);
      },
      error: () => { this.addFormVariants = []; }
    });
  }

  /** El usuario eligió (o quitó) una variante en el panel de alta. */
  onAddFormVariantChange(variantId: number | null): void {
    if (variantId == null) {
      this.addFormVariantId = null;
      this.addFormVariantName = null;
      this.addFormVariantSku = null;
      this.addFormVariantPriceApplied = false;
      const product = this.products.find(p => p.id === this.addFormProductId);
      if (product) {
        this.addFormUnitPrice = product.price;
        this.addFormItemCode = product.sku || '';
      }
      this.aplicarTierPricingAddForm();
      this.resolverDescuentoAddForm();
      return;
    }
    const variant = this.addFormVariants.find(v => v.id === variantId);
    if (!variant) return;
    this.addFormVariantId = variant.id;
    this.addFormVariantName = variant.attributesSummary || variant.name || variant.sku;
    this.addFormVariantSku = variant.sku;
    this.addFormUnitPrice = variant.effectivePrice;
    this.addFormItemCode = variant.sku;
    this.addFormVariantPriceApplied = true;
    this.addFormTierApplied = false;
    this.resolverDescuentoAddForm();
  }

  // ------------------------------------------------------------
  // (INC34) Añadir un PAQUETE (se expande en líneas de producto)
  // ------------------------------------------------------------

  /** Nombre del paquete del que proviene una línea (badge de trazabilidad). */
  bundleNameById(id: unknown): string {
    const bundleId = Number(id);
    if (!bundleId) return '';
    return this.bundles.find(b => b.id === bundleId)?.name || `Paquete #${bundleId}`;
  }

  abrirAgregarPaquete(): void {
    this.showAddItemForm = false;
    this.showAddBundleForm = true;
    this.bundleSearch = '';
    this.selectedBundleId = null;
    this.showBundleDropdown = false;
  }

  cerrarAgregarPaquete(): void {
    this.showAddBundleForm = false;
    this.showBundleDropdown = false;
  }

  onBundleSearchInput(term: string): void {
    this.bundleSearch = term;
    this.selectedBundleId = null;
    this.showBundleDropdown = true;
  }

  onBundleSearchEnter(event: Event): void {
    event.preventDefault();
    this.showBundleDropdown = true;
  }

  closeBundleDropdown(): void { setTimeout(() => { this.showBundleDropdown = false; }, 200); }

  get filteredBundlesForPanel(): ProductBundleResponse[] { return this.filterBundlesByTerm(this.bundleSearch); }

  seleccionarPaquete(bundle: ProductBundleResponse): void {
    this.selectedBundleId = bundle.id;
    this.bundleSearch = bundle.name + (bundle.sku ? ' — ' + bundle.sku : '');
    this.showBundleDropdown = false;
  }

  /**
   * Añade el paquete elegido EXPANDIÉNDOLO en una línea de ítem normal por componente
   * (`itemType='product'`, con `bundleId` para trazar el origen). No se reintroduce
   * 'bundle' como tipo de ítem (el Inc 24 lo quitó a propósito).
   *
   * La expansión se hace en cliente, no con POST /api/quotes/{id}/bundle-items, porque el
   * wizard debe soportar el alta: ahí la cotización todavía no existe (no hay quoteId) y el
   * usuario tiene que ver las líneas y los totales antes de guardar. El endpoint aplica la
   * misma regla de reparto, así que ambos caminos dan el mismo resultado.
   */
  confirmAddBundle(): void {
    const bundleId = this.selectedBundleId;
    if (!bundleId) return;
    const bundle = this.bundles.find(b => b.id === bundleId);
    if (!bundle) return;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.addingBundle = true;

    this.bundleApi.getBundleItems(bundleId, businessId).subscribe({
      next: (bundleItems) => {
        const componentes = bundleItems || [];
        if (componentes.length === 0) {
          this.addingBundle = false;
          this.toast.warning({ title: 'Paquete vacío', message: `El paquete "${bundle.name}" no tiene productos configurados.` });
          return;
        }
        this.expandirPaquete(bundle, componentes, businessId);
      },
      error: (err) => {
        this.addingBundle = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudieron cargar los productos del paquete' });
      }
    });
  }

  /**
   * Resuelve los productos del paquete (catálogo en memoria; los que no estén se piden al backend)
   * y agrega una línea por componente con el descuento del paquete ya repartido.
   */
  private expandirPaquete(bundle: ProductBundleResponse, componentes: ProductBundleItemResponse[], businessId: number): void {
    const faltantes = componentes
      .map(c => c.idProduct)
      .filter(id => !this.products.some(p => p.id === id));
    const unicos = Array.from(new Set(faltantes));

    const resolver = unicos.length === 0
      ? Promise.resolve([] as ProductResponse[])
      : Promise.all(unicos.map(id =>
          this.productoApi.getProductById(id, businessId).toPromise().catch(() => null)
        )).then(list => list.filter((p): p is ProductResponse => !!p));

    resolver.then((extra) => {
      const catalogo = [...this.products, ...extra];
      const lineas = componentes.map(c => {
        const product = catalogo.find(p => p.id === c.idProduct);
        const quantity = c.quantity || 1;
        const unitPrice = product?.price ?? 0;
        return {
          componente: c,
          product,
          quantity,
          unitPrice,
          importe: quantity * unitPrice
        };
      });

      const descuentos = this.repartirDescuentoDelPaquete(bundle, lineas.map(l => l.importe));

      lineas.forEach((l, i) => {
        this.items.push(this.createItemFormGroup({
          itemType: 'product',
          productId: l.componente.idProduct,
          // Trazabilidad del origen: la columna int_id_bundle ya existe en el backend.
          bundleId: bundle.id,
          itemName: l.product?.name || `Producto #${l.componente.idProduct}`,
          itemCode: l.product?.sku || undefined,
          category: l.product?.categoryName || undefined,
          description: l.product?.description || '',
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          // El descuento del paquete SIEMPRE como monto fijo: el backend no calcula con discountPercent.
          discountType: 'fixed',
          discountPercent: 0,
          discountAmount: descuentos[i],
          itemOrder: this.items.length
        }));
        // El precio del paquete manda: la línea nace fuera del automático para que el descuento
        // del Inc 40 no pise el reparto. Si más tarde el usuario cambia la cantidad o el precio de
        // la línea, el precio del paquete deja de sostenerse y el descuento se limpia — el selector
        // queda en "Sin descuento", a la vista, y desde ahí puede elegir otra regla.
        const idx = this.items.length - 1;
        if (descuentos[i] > 0) this.itemDiscountChoice[idx] = 'none';
      });

      this.addingBundle = false;
      this.showAddBundleForm = false;
      this.selectedBundleId = null;
      this.bundleSearch = '';
      this.toast.success({
        title: 'Paquete añadido',
        message: `${lineas.length} ${lineas.length === 1 ? 'ítem agregado' : 'ítems agregados'} desde "${bundle.name}"`
      });
    });
  }

  /**
   * Reparte el descuento del paquete entre sus líneas de forma PROPORCIONAL al importe de cada una,
   * devolviendo el monto (no el %) que va en `discountAmount` — el único campo que usa el
   * `calculateTotals` del backend.
   *
   * Descuento total = suma de los importes − `bundlePrice`. Si el paquete no tiene `bundlePrice`
   * (o no representa un ahorro), no se aplica descuento. El residuo de los redondeos se ajusta en
   * la ÚLTIMA línea para que la suma de las líneas dé exactamente `bundlePrice`.
   */
  private repartirDescuentoDelPaquete(bundle: ProductBundleResponse, importes: number[]): number[] {
    const sinDescuento = importes.map(() => 0);
    const bundlePrice = bundle.bundlePrice;
    if (bundlePrice == null) return sinDescuento;

    const totalBruto = this.round2(importes.reduce((a, b) => a + b, 0));
    const descuentoTotal = this.round2(totalBruto - bundlePrice);
    if (totalBruto <= 0 || descuentoTotal <= 0) return sinDescuento;

    const out = importes.map(importe => this.round2(descuentoTotal * (importe / totalBruto)));
    // Cuadre: el residuo de redondear cada línea se absorbe en la última.
    const repartido = this.round2(out.slice(0, -1).reduce((a, b) => a + b, 0));
    out[out.length - 1] = this.round2(descuentoTotal - repartido);
    return out;
  }

  // ------------------------------------------------------------
  // Precios por volumen (#18)
  // ------------------------------------------------------------

  /**
   * Si la fila es un producto con tramos de precio activos, consulta el
   * cálculo del backend para la cantidad actual y autocompleta el precio
   * unitario efectivo (total del tramo ÷ cantidad). El usuario puede
   * sobreescribirlo después. Falla en silencio: ante error se conserva el
   * precio base del producto.
   */
  aplicarTierPricing(index: number): void {
    const group = this.items.at(index) as FormGroup | null;
    if (!group) return;
    const value = group.value;
    if (value.itemType !== 'product' || !value.productId) {
      this.tierAppliedItems[index] = false;
      return;
    }
    const product = this.products.find(p => p.id === value.productId);
    if (!product?.usesTierPricing) {
      this.tierAppliedItems[index] = false;
      return;
    }
    const qty = Number(value.quantity);
    if (!qty || qty <= 0) return;

    this.tierPriceApi.calculateProductTierPrice(value.productId, qty).subscribe({
      next: (res) => {
        // Si la cantidad cambió mientras respondía el cálculo, este resultado ya no aplica.
        const currentQty = Number(group.get('quantity')?.value);
        if (currentQty !== qty) return;
        if (res.totalPrice > 0) {
          const unit = Math.round((res.totalPrice / qty) * 100) / 100;
          group.get('unitPrice')?.setValue(unit);
          this.tierAppliedItems[index] = true;
        } else {
          this.tierAppliedItems[index] = false;
        }
        // El precio pudo cambiar por el tramo: re-resolver el descuento vigente.
        this.resolverDescuentoLinea(index);
      },
      error: () => { this.tierAppliedItems[index] = false; }
    });
  }

  onItemQuantityChange(index: number): void {
    this.aplicarTierPricing(index);
    // El descuento por volumen depende de la cantidad: re-resolver aunque no haya tramos de precio.
    this.resolverDescuentoLinea(index);
  }

  // Precio unitario editado a mano: re-resolver el descuento vigente sobre el nuevo precio.
  onItemUnitPriceChange(index: number): void {
    // (INC33) El usuario tomó el control del precio: el request deja de mandar unitPrice=0 y el
    // backend respeta el valor tecleado en vez de resolver el precio efectivo de la variante.
    this.variantPriceApplied[index] = false;
    this.resolverDescuentoLinea(index);
  }

  onBundleSelect(index: number, bundleId: number | null): void {
    if (!bundleId) return;

    const bundle = this.bundles.find(b => b.id === bundleId);
    if (bundle) {
      const itemFormGroup = this.items.at(index) as FormGroup;
      itemFormGroup.patchValue({
        itemName: bundle.name,
        description: bundle.description || '',
        unitPrice: bundle.bundlePrice || 0
      });
    }
  }

  addBundleToQuote(bundleId: number, quantity: number): void {
    if (!this.quoteId) {
      this.error = 'Debe guardar la cotización primero antes de agregar bundles';
      return;
    }

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const request: AddBundleRequest = { bundleId, quantity };

    this.quoteApi.addBundleItem(this.quoteId, businessId, request).subscribe({
      next: (quote) => {
        this.currentQuote = quote;
        this.cargarQuote(this.quoteId!);
        this.successMessage = 'Bundle agregado correctamente';
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al agregar bundle';
      }
    });
  }

  // ------------------------------------------------------------
  // Paso 2 — Buscador + Filtros (filtrado en cliente sobre el FormArray)
  // ------------------------------------------------------------

  /** ¿Hay algún filtro de ítems activo? Mientras lo haya, se deshabilita el drag&drop. */
  get hasItemFilter(): boolean {
    return !!(this.itemSearchTerm.trim() || this.itemTypeFilter || this.itemCategoryFilter);
  }

  /** Categorías presentes en los ítems actuales (para el desplegable de filtro). */
  get itemCategories(): string[] {
    const set = new Set<string>();
    for (let i = 0; i < this.items.length; i++) {
      const raw = this.items.at(i).get('category')?.value;
      const cat = typeof raw === 'string' ? raw.trim() : '';
      if (cat) set.add(cat);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }

  /** ¿Un ítem (por índice) pasa los filtros activos? Busca por nombre/código/descripción. */
  private itemMatchesFilter(index: number): boolean {
    const v = this.items.at(index).value;
    if (this.itemTypeFilter && v.itemType !== this.itemTypeFilter) return false;
    if (this.itemCategoryFilter) {
      const cat = typeof v.category === 'string' ? v.category.trim() : '';
      if (cat !== this.itemCategoryFilter) return false;
    }
    const term = this.itemSearchTerm.trim().toLowerCase();
    if (term) {
      const haystack = [v.itemName, v.itemCode, v.description]
        .filter((s: unknown): s is string => typeof s === 'string')
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(term)) return false;
    }
    return true;
  }

  /** Índices de los ítems visibles según los filtros (el FormArray no se modifica). */
  get visibleItemIndices(): number[] {
    const out: number[] = [];
    for (let i = 0; i < this.items.length; i++) {
      if (this.itemMatchesFilter(i)) out.push(i);
    }
    return out;
  }

  /** Cantidad de ítems ocultos por el filtro (para el aviso "X ocultos"). */
  get hiddenItemCount(): number {
    return this.items.length - this.visibleItemIndices.length;
  }

  limpiarFiltroItems(): void {
    this.itemSearchTerm = '';
    this.itemTypeFilter = '';
    this.itemCategoryFilter = '';
  }

  // Local filter helpers
  /**
   * (INC_CRM_005 (5) inc42) El buscador de productos ignoraba por completo el tipo/subtipo
   * elegido: al entrar por "Servicios · Recursos" desplegaba TODO el catálogo. Ahora el
   * término se aplica sobre el subconjunto que corresponde al tipo de ítem:
   *
   *   product              → product.type ∈ {inventory, no_inventory}, acotado por `kind` (inc41)
   *   service_resource     → product.type = 'service' y subtype = 'resources'
   *   service_consulting   → product.type = 'service' y subtype = 'consulting'
   *
   * Tolerancia con datos históricos: un servicio sin `subtype` (anterior al Inc 53 de Productos)
   * cae en Recursos, y un producto no-servicio con `type` desconocido cae en Con Inventario, de
   * modo que ningún registro del catálogo queda inalcanzable desde el selector.
   */
  filterProductsByTerm(term: string, itemType?: string, kind?: ProductKind): ProductResponse[] {
    const base = itemType ? this.products.filter(p => this.matchesItemType(p, itemType, kind)) : this.products;
    const t = term.trim().toLowerCase();
    if (!t) return base.slice(0, 30);
    return base.filter(p => p.name.toLowerCase().includes(t) || (p.sku || '').toLowerCase().includes(t));
  }

  /** ¿El producto del catálogo pertenece al tipo (y subtipo) de ítem seleccionado? */
  private matchesItemType(p: ProductResponse, itemType: string, kind?: ProductKind): boolean {
    const type = (p.type || '').toLowerCase();
    const subtype = (p.subtype || '').toLowerCase();
    if (itemType === 'service_resource') return type === 'service' && subtype !== 'consulting';
    if (itemType === 'service_consulting') return type === 'service' && subtype === 'consulting';
    if (itemType === 'product') {
      if (type === 'service') return false;
      return (kind ?? 'inventory') === 'no_inventory' ? type === 'no_inventory' : type !== 'no_inventory';
    }
    return true;
  }

  filterBundlesByTerm(term: string): ProductBundleResponse[] {
    if (!term.trim()) return this.bundles.slice(0, 30);
    const t = term.toLowerCase();
    return this.bundles.filter(b => b.name.toLowerCase().includes(t) || (b.sku || '').toLowerCase().includes(t));
  }

  get filteredProductsForAddForm(): ProductResponse[] {
    return this.filterProductsByTerm(this.addFormProductSearch, this.addFormItemType, this.addFormProductKind);
  }
  get filteredBundlesForAddForm(): ProductBundleResponse[] { return this.filterBundlesByTerm(this.addFormBundleSearch); }

  // Add form
  openAddItemForm(): void {
    this.showAddItemForm = true;
    this.addFormItemType = 'custom';
    this.addFormProductKind = 'inventory';
    this.addFormItemName = '';
    this.addFormItemCode = '';
    this.addFormCategory = '';
    this.addFormDescription = '';
    this.addFormQuantity = 1;
    this.addFormUnit = '';
    this.addFormUnitPrice = 0;
    this.addFormDiscountPercent = 0;
    this.addFormDiscountAmount = 0;
    this.addFormProductId = null;
    this.addFormBundleId = null;
    this.addFormProductSearch = '';
    this.addFormBundleSearch = '';
    this.showAddFormProductDropdown = false;
    this.showAddFormBundleDropdown = false;
    this.addFormTierApplied = false;
    this.addFormDiscountRule = null;
    // (INC33) Panel limpio: sin producto elegido no hay variantes que ofrecer.
    this.addFormVariants = [];
    this.addFormVariantId = null;
    this.addFormVariantName = null;
    this.addFormVariantSku = null;
    this.addFormVariantPriceApplied = false;
    // (INC34) El alta de ítem y el alta de paquete son acciones separadas: no coexisten abiertas.
    this.showAddBundleForm = false;
  }

  cancelAddItemForm(): void { this.showAddItemForm = false; }

  onAddFormTypeChange(): void {
    this.addFormProductId = null;
    this.addFormBundleId = null;
    this.addFormProductSearch = '';
    this.addFormBundleSearch = '';
    this.addFormItemName = '';
    this.addFormItemCode = '';
    this.addFormCategory = '';
    this.addFormUnitPrice = 0;
    this.addFormDescription = '';
    this.showAddFormProductDropdown = false;
    this.showAddFormBundleDropdown = false;
    this.addFormDiscountRule = null;
    // (INC33) La variante depende del producto y del tipo 'product': al cambiar de tipo se descarta.
    this.addFormVariants = [];
    this.addFormVariantId = null;
    this.addFormVariantName = null;
    this.addFormVariantSku = null;
    this.addFormVariantPriceApplied = false;
  }

  // (Incidente 24) ¿El tipo del panel "Agregar ítem" es un servicio (Recursos o Consultoría)?
  get isServiceAddType(): boolean {
    return this.addFormItemType === 'service_resource' || this.addFormItemType === 'service_consulting';
  }

  // (Incidente 24) Botón "Servicios" del panel: entra a la subcategoría Recursos por defecto.
  selectServiceAddType(): void {
    if (!this.isServiceAddType) {
      this.addFormItemType = 'service_resource';
      this.onAddFormTypeChange();
    }
  }

  /**
   * (INC_CRM_005 (5) inc42) Alterna Recursos ↔ Consultoría en el panel. Antes solo asignaba
   * addFormItemType, sin limpiar nada: el producto elegido bajo el subtipo anterior se quedaba
   * pegado aunque ya no perteneciera al filtro. Ahora reinicia la selección como cualquier
   * otro cambio de tipo.
   */
  selectServiceAddSubtype(subtype: 'service_resource' | 'service_consulting'): void {
    if (this.addFormItemType === subtype) return;
    this.addFormItemType = subtype;
    this.onAddFormTypeChange();
  }

  // (Incidente 24) ¿Para este tipo de ítem aplica el selector de producto? (Producto y Servicios).
  // En 'product' es requerido; en servicios es opcional (autocompleta código/categoría).
  isProductLikeType(type: unknown): boolean {
    return type === 'product' || type === 'service_resource' || type === 'service_consulting';
  }

  onAddFormProductSearchInput(term: string): void {
    this.addFormProductSearch = term;
    this.showAddFormProductDropdown = true;
  }

  onAddFormProductSearchEnter(event: Event): void {
    event.preventDefault();
    this.showAddFormProductDropdown = true;
  }

  onAddFormBundleSearchInput(term: string): void {
    this.addFormBundleSearch = term;
    this.showAddFormBundleDropdown = true;
  }

  onAddFormBundleSearchEnter(event: Event): void {
    event.preventDefault();
    this.showAddFormBundleDropdown = true;
  }

  selectProductForAddForm(product: ProductResponse): void {
    this.addFormProductId = product.id;
    this.addFormProductSearch = product.name + (product.sku ? ' — ' + product.sku : '');
    this.addFormItemName = product.name;
    this.addFormItemCode = product.sku || '';
    this.addFormCategory = product.categoryName || '';
    this.addFormDescription = product.description || '';
    this.addFormUnitPrice = product.price;
    this.showAddFormProductDropdown = false;
    this.aplicarPrecioDeListaAddForm(product.id);
    this.aplicarTierPricingAddForm();
    // (INC33) Variantes del producto elegido: si tiene, aparece el selector de variante del panel.
    this.cargarVariantesAddForm();
    // Resolver el descuento vigente sobre el precio base del panel (los flujos async de
    // lista/tramos vuelven a resolver si cambian el precio).
    this.resolverDescuentoAddForm();
  }

  /** Tier pricing para el panel "Agregar ítem": misma lógica que las filas, sobre el estado del panel. */
  aplicarTierPricingAddForm(): void {
    this.addFormTierApplied = false;
    if (this.addFormItemType !== 'product' || !this.addFormProductId) return;
    const product = this.products.find(p => p.id === this.addFormProductId);
    if (!product?.usesTierPricing) return;
    const qty = Number(this.addFormQuantity);
    if (!qty || qty <= 0) return;
    const productId = this.addFormProductId;

    this.tierPriceApi.calculateProductTierPrice(productId, qty).subscribe({
      next: (res) => {
        if (this.addFormProductId !== productId || Number(this.addFormQuantity) !== qty) return;
        if (res.totalPrice > 0) {
          this.addFormUnitPrice = Math.round((res.totalPrice / qty) * 100) / 100;
          this.addFormTierApplied = true;
        }
        // El precio pudo cambiar por el tramo: re-resolver el descuento del panel.
        this.resolverDescuentoAddForm();
      },
      error: () => { /* conservar precio base */ }
    });
  }

  // Cantidad del panel "Agregar ítem" cambiada: recalcular tramo y descuento vigente.
  onAddFormQuantityChange(): void {
    this.aplicarTierPricingAddForm();
    this.resolverDescuentoAddForm();
  }

  // Precio unitario del panel editado a mano: re-resolver el descuento vigente.
  onAddFormUnitPriceChange(): void {
    this.resolverDescuentoAddForm();
  }

  selectBundleForAddForm(bundle: ProductBundleResponse): void {
    this.addFormBundleId = bundle.id;
    this.addFormBundleSearch = bundle.name + (bundle.sku ? ' — ' + bundle.sku : '');
    this.addFormItemName = bundle.name;
    this.addFormDescription = bundle.description || '';
    this.addFormUnitPrice = bundle.bundlePrice || 0;
    this.showAddFormBundleDropdown = false;
  }

  closeAddFormProductDropdown(): void { setTimeout(() => { this.showAddFormProductDropdown = false; }, 200); }
  closeAddFormBundleDropdown(): void { setTimeout(() => { this.showAddFormBundleDropdown = false; }, 200); }

  confirmAddItem(): void {
    if (!this.addFormItemName.trim()) return;
    if (this.addFormQuantity <= 0) return;
    // (Incidente 24) Productos: el productId es requerido (servicios y custom lo permiten vacío).
    if (this.addFormItemType === 'product' && !this.addFormProductId) {
      this.toast.error({ title: 'Producto requerido', message: 'Selecciona un producto del catálogo para este ítem.' });
      return;
    }
    const group = this.createItemFormGroup({
      itemType: this.addFormItemType,
      productId: this.addFormProductId ?? undefined,
      bundleId: this.addFormBundleId ?? undefined,
      itemName: this.addFormItemName,
      itemCode: this.addFormItemCode || undefined,
      category: this.addFormCategory || undefined,
      description: this.addFormDescription,
      quantity: this.addFormQuantity,
      unit: this.addFormUnit,
      unitPrice: this.addFormUnitPrice,
      discountPercent: this.addFormDiscountPercent,
      discountAmount: this.addFormDiscountAmount,
      // (INC33) La variante solo viaja en ítems de tipo 'product' (el backend rechaza el resto).
      variantId: this.addFormItemType === 'product' ? this.addFormVariantId : null,
      // (INC32) Trazabilidad de la regla auto-aplicada en el panel.
      discountRuleId: this.addFormDiscountRule?.ruleId ?? null,
      // itemOrder secuencial 0..n, consistente con la reasignación del drag & drop
      itemOrder: this.items.length
    });
    group.patchValue(
      { variantName: this.addFormVariantName, variantSku: this.addFormVariantSku },
      { emitEvent: false }
    );
    this.items.push(group);
    const newIndex = this.items.length - 1;
    this.tierAppliedItems[newIndex] = this.addFormTierApplied;
    // Trasladar a la fila el estado resuelto en el panel (descuento de regla y variante).
    if (this.addFormDiscountRule?.applied) {
      this.itemDiscountRules[newIndex] = this.addFormDiscountRule;
    }
    if (this.addFormVariants.length > 0) this.itemVariants[newIndex] = this.addFormVariants;
    this.variantPriceApplied[newIndex] = this.addFormVariantPriceApplied;
    this.addFormTierApplied = false;
    this.addFormDiscountRule = null;
    this.showAddItemForm = false;
  }

  // Inline item expand/collapse
  toggleItemExpand(index: number): void {
    if (this.expandedItems.has(index)) {
      this.expandedItems.delete(index);
    } else {
      this.expandedItems.add(index);
      const item = this.items.at(index).value;
      if (item.itemType === 'product' && item.productId) {
        const p = this.products.find(p => p.id === item.productId);
        this.itemProductSearchTerms[index] = p ? p.name + (p.sku ? ' — ' + p.sku : '') : '';
        // (INC33/INC32) Variantes y reglas se piden al ABRIR la fila, no al cargar la cotización:
        // así una cotización con muchos ítems no dispara 2N peticiones de golpe.
        if (this.itemVariants[index] === undefined) this.cargarVariantesLinea(index);
        if (this.itemApplicableRules[index] === undefined) this.cargarReglasAplicables(index);
      }
      if (item.itemType === 'bundle' && item.bundleId) {
        const b = this.bundles.find(b => b.id === item.bundleId);
        this.itemBundleSearchTerms[index] = b ? b.name + (b.sku ? ' — ' + b.sku : '') : '';
      }
    }
  }

  isItemExpanded(index: number): boolean { return this.expandedItems.has(index); }

  onItemProductSearchInput(index: number, term: string): void {
    this.itemProductSearchTerms[index] = term;
    this.showItemProductDropdown[index] = true;
  }

  onItemProductSearchEnter(index: number, event: Event): void {
    event.preventDefault();
    this.showItemProductDropdown[index] = true;
  }

  onItemBundleSearchInput(index: number, term: string): void {
    this.itemBundleSearchTerms[index] = term;
    this.showItemBundleDropdown[index] = true;
  }

  onItemBundleSearchEnter(index: number, event: Event): void {
    event.preventDefault();
    this.showItemBundleDropdown[index] = true;
  }

  selectProductForItem(index: number, product: ProductResponse): void {
    this.itemProductSearchTerms[index] = product.name + (product.sku ? ' — ' + product.sku : '');
    this.showItemProductDropdown[index] = false;
    (this.items.at(index) as FormGroup).patchValue({
      productId: product.id, itemName: product.name,
      description: product.description || '', unitPrice: product.price,
      itemCode: product.sku || '', category: product.categoryName || '',
      // Cambió el producto: la variante del anterior ya no aplica.
      variantId: null, variantName: null, variantSku: null
    });
    this.variantPriceApplied[index] = false;
    this.aplicarPrecioDeLista(index, product.id);
    this.aplicarTierPricing(index);
    // (INC33) Variantes del producto elegido: si tiene, se muestra el selector de variante.
    this.cargarVariantesLinea(index);
    // (INC32) Reglas elegibles del producto: el control explícito arranca en automático.
    this.resetControlDescuentoLinea(index);
    this.cargarReglasAplicables(index);
    // Resolver el descuento vigente sobre el precio base (los flujos async de lista/tramos
    // vuelven a resolver si cambian el precio). Cubre el caso sin lista y sin tramos.
    this.resolverDescuentoLinea(index);
  }

  selectBundleForItem(index: number, bundle: ProductBundleResponse): void {
    this.itemBundleSearchTerms[index] = bundle.name + (bundle.sku ? ' — ' + bundle.sku : '');
    this.showItemBundleDropdown[index] = false;
    (this.items.at(index) as FormGroup).patchValue({
      bundleId: bundle.id, itemName: bundle.name,
      description: bundle.description || '', unitPrice: bundle.bundlePrice || 0
    });
  }

  closeItemProductDropdown(index: number): void { setTimeout(() => { this.showItemProductDropdown[index] = false; }, 200); }
  closeItemBundleDropdown(index: number): void { setTimeout(() => { this.showItemBundleDropdown[index] = false; }, 200); }

  getFilteredProductsForItem(index: number): ProductResponse[] {
    return this.filterProductsByTerm(
      this.itemProductSearchTerms[index] || '',
      this.items.at(index)?.get('itemType')?.value,
      this.productKindFor(index));
  }

  // ---- (INC_CRM_005 (5) inc41) Subtipo de "Productos" en las líneas ya agregadas ----

  /**
   * Subtipo de producto de la línea `index`. Si el usuario no lo eligió a mano, se DEDUCE del
   * producto ya referenciado por la línea, para que al editar una cotización guardada el radio
   * refleje el ítem real en vez de caer siempre en "Con Inventario". Sin producto → Con Inventario.
   */
  productKindFor(index: number): ProductKind {
    const explicit = this.itemProductKinds[index];
    if (explicit) return explicit;
    const productId = this.items.at(index)?.get('productId')?.value;
    const product = productId ? this.products.find(p => p.id === Number(productId)) : null;
    return (product?.type || '').toLowerCase() === 'no_inventory' ? 'no_inventory' : 'inventory';
  }

  /** Cambia el subtipo de producto de la línea y descarta el producto elegido con el filtro anterior. */
  setProductKindFor(index: number, kind: ProductKind): void {
    if (this.productKindFor(index) === kind) return;
    this.itemProductKinds[index] = kind;
    this.onItemTypeChange(index);
  }

  /** Cambia el subtipo de producto del panel "Agregar ítem" y limpia la selección previa. */
  setAddFormProductKind(kind: ProductKind): void {
    if (this.addFormProductKind === kind) return;
    this.addFormProductKind = kind;
    this.onAddFormTypeChange();
  }

  getFilteredBundlesForItem(index: number): ProductBundleResponse[] {
    return this.filterBundlesByTerm(this.itemBundleSearchTerms[index] || '');
  }

  // ------------------------------------------------------------
  // Distribución de ítems por categoría (V76) — solo lectura, calculada en el front
  // ------------------------------------------------------------

  /**
   * Distribución de los ítems ACTUALES del form agrupados por categoría. Los ítems sin categoría
   * se agrupan como "Sin categoría" (siempre al final). Cada grupo trae la cantidad de ítems y el
   * subtotal acumulado (reusa calculateItemSubtotal, que ya descuenta por línea). Se recalcula en
   * cada ciclo de detección de cambios al cambiar ítems/cantidades/precios.
   */
  get categoryDistribution(): { category: string; count: number; subtotal: number }[] {
    const NO_CAT = 'Sin categoría';
    const order: string[] = [];
    const map = new Map<string, { count: number; subtotal: number }>();
    for (let i = 0; i < this.items.length; i++) {
      const raw = this.items.at(i).get('category')?.value;
      const cat = (typeof raw === 'string' && raw.trim()) ? raw.trim() : NO_CAT;
      if (!map.has(cat)) {
        map.set(cat, { count: 0, subtotal: 0 });
        order.push(cat);
      }
      const acc = map.get(cat)!;
      acc.count += 1;
      acc.subtotal += this.calculateItemSubtotal(i);
    }
    const sorted = order.filter(c => c !== NO_CAT);
    if (map.has(NO_CAT)) sorted.push(NO_CAT);
    return sorted.map(category => ({ category, ...map.get(category)! }));
  }

  /**
   * (Incidente 14) true si AL MENOS UN ítem tiene una categoría real definida. Cuando ningún
   * ítem tiene categoría, la tarjeta "Distribución por categorías" se oculta por completo para
   * no mostrar un único bloque "Sin categoría" que solo genera ruido visual. Si hay categorías
   * reales, el grupo "Sin categoría" sí se muestra (al final) para diferenciar los ítems sueltos.
   */
  get hasRealCategories(): boolean {
    for (let i = 0; i < this.items.length; i++) {
      const raw = this.items.at(i).get('category')?.value;
      if (typeof raw === 'string' && raw.trim()) return true;
    }
    return false;
  }

  // ------------------------------------------------------------
  // (Incidente 15) Desglose de pago a crédito en la card "Totales" del editor.
  // Espeja la lógica de <app-quote-document> para que el usuario vea, ANTES de enviar,
  // el mismo desglose financiero que recibirá el cliente (anticipo, saldo financiado,
  // intereses, total con financiamiento y total convertido por tipo de cambio).
  // Es solo informativo: no altera subtotal/impuesto/total ni los ítems.
  //   anticipo               = total × advancePercent / 100   (advancePercent null → 0)
  //   saldo (financiado)     = total − anticipo
  //   interes                = saldo × interestRatePercent / 100
  //   totalConFinanciamiento = total + interes
  // ------------------------------------------------------------

  private numCtrl(name: string): number | null {
    const raw = this.form.get(name)?.value;
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  private round2(n: number): number {
    return Math.round((n + Number.EPSILON) * 100) / 100;
  }

  /**
   * (INC_CRM_005 (5) inc43) true si hay anticipo (> 0) sobre un total conocido, sea cual sea la
   * forma de pago. Antes exigía CRÉDITO + plazo, así que con "Contado" el usuario escribía el
   * anticipo, lo veía impreso como porcentaje en el PDF y nunca se enteraba de a cuánto equivalía
   * ni de cuánto quedaba pendiente.
   */
  get showAdvanceBreakdown(): boolean {
    const pct = this.numCtrl('advancePercent');
    return pct != null && pct > 0 && this.calculateTotal() > 0;
  }

  /** true si es a crédito con anticipo y plazo definidos (bloque base de crédito). */
  get showCreditBreakdown(): boolean {
    return this.form.get('paymentType')?.value === 'CREDITO'
      && this.numCtrl('advancePercent') != null
      && this.numCtrl('creditTermDays') != null;
  }

  /** Título del bloque de desglose: solo habla de crédito cuando el pago realmente lo es. */
  get breakdownTitle(): string {
    return this.form.get('paymentType')?.value === 'CREDITO'
      ? 'Desglose de pago a crédito'
      : 'Desglose de pago';
  }

  /** true si es a crédito con tasa de interés (> 0) y total mayor a cero. */
  get showFinancingBreakdown(): boolean {
    const rate = this.numCtrl('interestRatePercent');
    return this.form.get('paymentType')?.value === 'CREDITO'
      && rate != null && rate > 0
      && this.calculateTotal() > 0;
  }

  /** Plazo de crédito en días (para las etiquetas del desglose). */
  get creditTermDaysValue(): number | null {
    return this.numCtrl('creditTermDays');
  }

  /** % de anticipo (para la etiqueta del anticipo). */
  get advancePercentValue(): number | null {
    return this.numCtrl('advancePercent');
  }

  /** % de interés (para la etiqueta de intereses). */
  get interestRatePercentValue(): number | null {
    return this.numCtrl('interestRatePercent');
  }

  /** Monto del anticipo = total × advancePercent / 100 (advancePercent null → 0). */
  get advanceAmount(): number {
    const pct = this.numCtrl('advancePercent');
    return pct != null ? this.calculateTotal() * (pct / 100) : 0;
  }

  /** Saldo restante / financiado (sin redondear) = total − anticipo. */
  private get remainingBalanceRaw(): number {
    return this.calculateTotal() - this.advanceAmount;
  }

  /** Saldo financiado (redondeado) = total − anticipo. */
  get financedBalance(): number {
    return this.round2(this.remainingBalanceRaw);
  }

  /** Intereses = saldo financiado × interestRatePercent / 100 (HALF_UP a 2 decimales). */
  get interestAmount(): number {
    const rate = this.numCtrl('interestRatePercent') ?? 0;
    return this.round2(this.remainingBalanceRaw * (rate / 100));
  }

  /** Total con financiamiento = total + intereses (HALF_UP a 2 decimales). */
  get totalWithFinancing(): number {
    return this.round2(this.calculateTotal() + this.interestAmount);
  }

  /** Tipo de cambio ingresado (para la etiqueta del total ajustado). */
  get exchangeRateValue(): number | null {
    return this.numCtrl('exchangeRate');
  }

  // ------------------------------------------------------------
  // (INC_CRM_005 (5) inc44) Tipo de cambio desde el catálogo de monedas
  // ------------------------------------------------------------
  // Antes era un input numérico libre: el usuario tenía que saberse el tipo de cambio de memoria
  // y teclearlo, sin relación con la moneda elegida. Ahora se ofrece un desplegable con las tasas
  // del catálogo de Productos (currency.rateUsd, la misma fuente que usa la lista de precios):
  //
  //     tasa(origen → destino) = rateUsd(destino) / rateUsd(origen)
  //
  // donde el origen es la moneda de la cotización. El destino sugerido por defecto es la moneda
  // base del negocio, que es el caso que describe la ayuda del campo ("solo si la moneda difiere
  // de la base del negocio"). Se conserva la opción "Otro (manual)" porque el valor persistido
  // sigue siendo un número suelto y hay negocios que pactan una tasa distinta a la del catálogo.

  /** Opción del desplegable de tipo de cambio: moneda destino + tasa calculada del catálogo. */
  get exchangeRateOptions(): { currencyId: number; code: string; rate: number; label: string }[] {
    const from = this.quoteCurrency;
    if (!from) return [];
    const fromRate = this.rateUsdOf(from);
    if (fromRate == null) return [];

    const options = this.currencies
      .filter(c => c.id !== from.id)
      .map(c => {
        const toRate = this.rateUsdOf(c);
        if (toRate == null) return null;
        const rate = this.round4(toRate / fromRate);
        if (!(rate > 0)) return null;
        const code = c.code || c.prefix || String(c.id);
        const isBase = c.id === this.businessDefaultCurrencyId;
        return {
          currencyId: c.id,
          code,
          rate,
          label: `1 ${from.code || from.prefix} = ${rate} ${code}`
            + (isBase ? ' — moneda base del negocio' : '')
        };
      })
      .filter((o): o is { currencyId: number; code: string; rate: number; label: string } => o !== null);

    // El catálogo trae ~60 monedas en orden de inserción: sin ordenar, encontrar la tasa que
    // interesa es peor que teclearla. Se ordena por código y se sube la moneda base del negocio,
    // que es el destino de conversión habitual.
    options.sort((a, b) => {
      const aBase = a.currencyId === this.businessDefaultCurrencyId;
      const bBase = b.currencyId === this.businessDefaultCurrencyId;
      if (aBase !== bBase) return aBase ? -1 : 1;
      return a.code.localeCompare(b.code);
    });
    return options;
  }

  /** Moneda seleccionada en la cotización (origen de la conversión). */
  private get quoteCurrency(): CurrencyResponse | null {
    const selectedId = this.form.get('currencyId')?.value;
    return (selectedId ? this.currencies.find(c => c.id === selectedId) : null) ?? null;
  }

  /** Tasa vs USD del catálogo (currency.rateUsd llega como string en el DTO). null si no es usable. */
  private rateUsdOf(currency: CurrencyResponse): number | null {
    const raw = currency.rateUsd;
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  private round4(n: number): number {
    return Math.round((n + Number.EPSILON) * 10000) / 10000;
  }

  /**
   * Valor del `<select>`: el id de la moneda destino guardada (V188); si la cotización es
   * anterior y solo tiene el número, se deriva buscando la tasa del catálogo que coincide.
   * 'manual' cuando hay una tasa que no corresponde a ninguna moneda, '' si no hay tipo de cambio.
   */
  get exchangeRateSelection(): string {
    if (this.exchangeRateManual) return 'manual';
    const current = this.numCtrl('exchangeRate');
    if (current == null || current <= 0) return '';
    // V188: la moneda destino ya se persiste, así que manda sobre la derivación por tasa
    // (que es ambigua: varias monedas ancladas al dólar comparten la misma tasa).
    const savedCurrencyId = this.form.get('exchangeRateCurrencyId')?.value;
    if (savedCurrencyId != null) return String(savedCurrencyId);
    const match = this.exchangeRateOptions.find(o => Math.abs(o.rate - current) < 0.00005);
    return match ? String(match.currencyId) : 'manual';
  }

  /** true mientras el usuario edita el tipo de cambio a mano (revela el input numérico). */
  exchangeRateManual = false;

  /**
   * (V188) Moneda a la que convierte el tipo de cambio actual, tomada del catálogo. Es la que
   * etiqueta el total convertido en la vista previa, el documento y el PDF. Con una tasa
   * tecleada a mano no hay moneda destino y devuelve null.
   */
  private get exchangeRateTargetCurrency(): CurrencyResponse | null {
    const selection = this.exchangeRateSelection;
    if (!selection || selection === 'manual') return null;
    return this.currencies.find(c => String(c.id) === selection) ?? null;
  }

  /** Código de la moneda de conversión (ej. 'USD'), o null si la tasa es manual. */
  get exchangeRateTargetCode(): string | null {
    const c = this.exchangeRateTargetCurrency;
    return c ? (c.code || c.prefix || null) : null;
  }

  /**
   * Rótulo del total convertido: nombra la moneda de conversión ("Total en Dólar
   * Estadounidense (USD)") y cae al rótulo con la tasa cuando se tecleó a mano.
   */
  get convertedTotalLabel(): string {
    const c = this.exchangeRateTargetCurrency;
    const name = c?.name?.trim() || null;
    const code = this.exchangeRateTargetCode;
    if (name && code) return `Total en ${name} (${code})`;
    if (name) return `Total en ${name}`;
    if (code) return `Total en ${code}`;
    return `Total (T.C. ${this.exchangeRateValue})`;
  }

  /** Símbolo de la moneda de conversión, con espacio. Vacío si la tasa fue manual. */
  get convertedTotalSymbol(): string {
    const symbol = this.exchangeRateTargetCurrency?.prefix?.trim();
    return symbol ? `${symbol} ` : '';
  }

  onExchangeRateSelectionChange(value: string): void {
    if (value === 'manual') {
      this.exchangeRateManual = true;
      // Tasa pactada a mano: no hay moneda del catálogo que la respalde.
      this.form.patchValue({ exchangeRateCurrencyId: null });
      return;
    }
    this.exchangeRateManual = false;
    if (!value) {
      this.form.patchValue({ exchangeRate: null, exchangeRateCurrencyId: null });
      return;
    }
    const option = this.exchangeRateOptions.find(o => String(o.currencyId) === value);
    this.form.patchValue({
      exchangeRate: option ? option.rate : null,
      exchangeRateCurrencyId: option ? option.currencyId : null
    });
  }

  /**
   * La moneda de la cotización cambió: el tipo de cambio anterior era una tasa DESDE la moneda
   * anterior, así que ya no significa nada. Se re-sugiere la tasa hacia la moneda base del negocio
   * y, si no hay ninguna calculable, se limpia. No pisa un valor puesto a mano.
   */
  onCurrencyChangeResetExchangeRate(): void {
    // Se ata al evento `change` del <select> (no a valueChanges) para que solo dispare con una
    // acción del usuario y no cuando cargarQuote() parchea la moneda guardada. El setTimeout deja
    // que el ControlValueAccessor escriba currencyId antes de recalcular: el orden entre el
    // listener del accessor y el del template no está garantizado.
    setTimeout(() => {
      if (this.exchangeRateManual) return;
      const suggested = this.exchangeRateOptions.find(o => o.currencyId === this.businessDefaultCurrencyId);
      this.form.patchValue({
        exchangeRate: suggested ? suggested.rate : null,
        exchangeRateCurrencyId: suggested ? suggested.currencyId : null
      });
    });
  }

  /** Total convertido por tipo de cambio (null si no aplica exchangeRate). */
  get convertedTotal(): number | null {
    const rate = this.numCtrl('exchangeRate');
    if (rate == null || rate <= 0) return null;
    return this.calculateTotal() * rate;
  }

  // ------------------------------------------------------------
  // Importar ítems desde una plantilla de contenido (V76)
  // ------------------------------------------------------------

  openImportItemsModal(): void {
    this.showImportItemsModal = true;
    this.importingTemplateId = null;
    this.cargarContentTemplates();
  }

  closeImportItemsModal(): void {
    this.showImportItemsModal = false;
  }

  private cargarContentTemplates(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    this.loadingContentTemplates = true;
    this.contentTemplateApi.getAllTemplates(businessId).subscribe({
      next: (templates) => {
        this.contentTemplates = (templates || []).filter(t => t.isActive !== false);
        this.loadingContentTemplates = false;
      },
      error: (err) => {
        this.loadingContentTemplates = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudieron cargar las plantillas de contenido' });
      }
    });
  }

  /**
   * Trae los ítems de la plantilla seleccionada y los ANEXA al final de la lista (no reemplaza).
   * itemCode/category/descuentos/itemTaxRate van vacíos: el backend autocompleta código y categoría
   * para productos al guardar. Toast con la cantidad importada.
   */
  importFromContentTemplate(template: QuoteContentTemplateResponse): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    this.importingTemplateId = template.id;
    this.contentTemplateApi.getTemplateById(template.id, businessId).subscribe({
      next: (full) => {
        const items = full.items || [];
        items.forEach((it, idx) => {
          this.items.push(this.createItemFormGroup({
            itemName: it.itemName,
            description: it.description,
            quantity: it.quantity ?? 1,
            unitPrice: it.unitPrice ?? 0,
            unit: it.unit || '',
            productId: it.productId ?? undefined,
            itemType: (it.itemType as 'product' | 'bundle' | 'custom') || 'custom',
            // itemOrder secuencial al final de la lista existente.
            itemOrder: this.items.length + idx
          }));
        });
        this.importingTemplateId = null;
        this.showImportItemsModal = false;
        this.toast.success({ title: 'Ítems importados', message: `${items.length} ${items.length === 1 ? 'ítem importado' : 'ítems importados'}` });
      },
      error: (err) => {
        this.importingTemplateId = null;
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudieron importar los ítems de la plantilla' });
      }
    });
  }
}
